// AUTO-GENERATED from content/npcs/ + content/mobs/ — do not edit by hand

export interface NpcQuestLine {
  beforeQuest?: string;
  duringQuest?: string;
  afterQuest?: string;
  text: string;
}

export interface NPCDataConfig {
  id: string;
  name: string;
  role: import('../../npc/npcRoles').NpcRole;
  x: number;
  y: number;
  sprite: string;
  dialogue: { greeting: string; quests: string; complete: string; goodbye: string };
  /** Greetings keyed by the reader's quest state (content/npcs lines; src/shared/npc/npcQuestLines.ts). */
  lines?: NpcQuestLine[];
  availableQuests: string[];
  shopItems?: string[];
  trainerClasses?: string[];
}

export type MobType = 'normal' | 'elite' | 'rare' | 'boss' | 'critter' | 'training';
export type AiType =
  | 'passive' | 'aggressive' | 'patrol' | 'stationary' | 'flee'
  | 'skittish' | 'brawler' | 'charger' | 'skirmisher' | 'caster' | 'juggernaut';

export interface BossPhase {
  hpThreshold: number;
  name: string;
  abilities?: string[];
  damageMultiplier?: number;
  speedMultiplier?: number;
  color?: string;
  /** Rock armour on entering the phase: damage taken × armorDamageMultiplier for armorMs. */
  armorMs?: number;
  armorDamageMultiplier?: number;
}

export interface MobAbilityRefConfig {
  id: string;
  weight: number;
}

/** Per-mob death-beat override ([<mob>.death] TOML — death-and-kill-beats
 *  spec §3.2). Consumed by KillBeatDirector ON TOP of its default derivation
 *  (killing-blow school + mob_type tier); every field optional. */
export interface MobDeathConfig {
  /** Force a school-style beat regardless of the killing blow. */
  style?: 'physical' | 'holy' | 'nature' | 'shadow' | 'arcane' | 'fire' | 'frost';
  /** Flourish particle scale multiplier (bosses get bigger beats). */
  scale?: number;
  /** Ghost dissolve-program stretch (ms). */
  durationMs?: number;
  /** Default true; training dummies set false (no lingering remnant). */
  soulWisp?: boolean;
}

export interface MobAbilityConfig {
  name: string;
  baseDamage: number;
  cooldownMs: number;
  range: number;
  windUpMs?: number;
  /** What the landing wind-up hits (default: the target). */
  shape?: 'shockwave' | 'lob' | 'cloud';
  /** Lob: hit radius around the wind-up target point; cloud: around the mob. */
  impactRadius?: number;
  /** Cloud: lingers this long after landing, hitting every tickMs. */
  lingerMs?: number;
  tickMs?: number;
}

export interface MobDataConfig {
  id: string;
  name: string;
  type: string;
  mobType?: MobType;
  aiType?: AiType;
  aggroRange?: number;
  respawnTime?: number;
  abilities?: MobAbilityRefConfig[];
  /** Per-mob attack damage override; multiplied by level at runtime. */
  baseDamage?: number;
  /** Per-mob attack range override (pixels). */
  attackRange?: number;
  /** Per-mob AI knobs (plan 08); unset → template/global default. */
  chaseSpeedMult?: number;
  leashRange?: number;
  reactionMs?: number;
  attackIntervalMs?: number;
  jumpThreshold?: number;
  socialAggroRange?: number;
  /** Patrol: half-width of the default route around the spawn point. */
  patrolSpan?: number;
  /** Caster: the distance it backs off to when crowded. */
  keepDistance?: number;
  /** D37: never fights back when hit (content opt-out; every other mob does). */
  harmless?: boolean;
  /** On death, splits into `count` mobs of id `into` (the slime). */
  split?: { into: string; count: number };
  phases?: BossPhase[];
  death?: MobDeathConfig;
  hp: number;
  level: number;
  xp: number;
  color: string;
  width: number;
  height: number;
  movementStats?: {
    moveSpeed: number; jumpPower: number; gravityScale: number;
    terminalVelocity: number; acceleration: number; deceleration: number;
  };
}

export const MOB_ABILITY_CONFIG: Record<string, MobAbilityConfig> = {
  'pig_bite': {
    name: 'Pig Bite',
    baseDamage: 5,
    cooldownMs: 1500,
    range: 60,
  },
  'pig_charge': {
    name: 'Pig Charge',
    baseDamage: 12,
    cooldownMs: 8000,
    range: 200, windUpMs: 800,
  },
  'snail_goo': {
    name: 'Goo Spit',
    baseDamage: 3,
    cooldownMs: 2000,
    range: 100,
  },
  'dummy_punch': {
    name: 'Training Punch',
    baseDamage: 1,
    cooldownMs: 2000,
    range: 70,
  },
  'keeper_rake': {
    name: 'Keeper\'s Rake',
    baseDamage: 6,
    cooldownMs: 2500,
    range: 120,
  },
  'keeper_slam': {
    name: 'Hollow Slam',
    baseDamage: 11,
    cooldownMs: 9000,
    range: 160, windUpMs: 900,
  },
  'hollow_wail': {
    name: 'Hollow Wail',
    baseDamage: 4,
    cooldownMs: 5000,
    range: 170, windUpMs: 350,
  },
  'hollow_grasp': {
    name: 'Hollow Grasp',
    baseDamage: 9,
    cooldownMs: 6000,
    range: 180, windUpMs: 600,
  },
  'golem_ground_slam': {
    name: 'Ground Slam',
    baseDamage: 9,
    cooldownMs: 7000,
    range: 170, windUpMs: 900, shape: 'shockwave',
  },
  'golem_slam_awakened': {
    name: 'Ground Slam',
    baseDamage: 9,
    cooldownMs: 4500,
    range: 200, windUpMs: 650, shape: 'shockwave',
  },
  'golem_boulder_toss': {
    name: 'Boulder Toss',
    baseDamage: 7,
    cooldownMs: 6000,
    range: 460, windUpMs: 800, shape: 'lob', impactRadius: 60,
  },
  'mushroom_spores': {
    name: 'Spore Puff',
    baseDamage: 3,
    cooldownMs: 7000,
    range: 75, windUpMs: 700, shape: 'cloud', impactRadius: 75, lingerMs: 1500, tickMs: 500,
  },
  'blightroot_eruption': {
    name: 'Blightroot Eruption',
    baseDamage: 8,
    cooldownMs: 8000,
    range: 180, windUpMs: 900, shape: 'shockwave',
  },
  'blightroot_spores': {
    name: 'Blightroot Spores',
    baseDamage: 2,
    cooldownMs: 9000,
    range: 90, windUpMs: 800, shape: 'cloud', impactRadius: 90, lingerMs: 2000, tickMs: 500,
  },
};

export const NPC_CONFIG: Record<string, NPCDataConfig> = {
  'tomas': {
    id: 'tomas',
    name: 'Tomas the Researcher',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 200,
    y: 400,
    sprite: 'npc_scientist',
    dialogue: {
      greeting: 'You made it out of the Old Vault. Good — Elder Aislin said the hearth would wake for someone before dawn.',
      quests: 'The west pasture in First Field is overrun. Thin the herd, then report to Warden Lark at the east survey post.',
      complete: 'The field road is open because you chose to walk it. That matters here.',
      goodbye: 'Keep east once you enter the field. Maria can provision you before you go.',
    },
    lines: [
      { afterQuest: 'relight_the_coil', text: 'Word came down the coast that the Coil Light turns again. I\'ve written your name in my notes beside it. The hearth woke for the right person.' },
      { afterQuest: 'first_hunt', text: 'Warden Lark\'s runner brought the ledger: eight marks, and the west fence holding. I\'ll confess I had a page ready in case you didn\'t come back. I\'ve torn it out.' },
      { duringQuest: 'first_hunt', text: 'The west pasture won\'t thin itself. Eight from the herd, then report to Warden Lark at the east survey post. I\'ll want to hear how the fence held.' },
    ],
    availableQuests: ['first_hunt'],
  },
  'sophia': {
    id: 'sophia',
    name: 'Sophia the Explorer',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 400,
    y: 400,
    sprite: 'npc_explorer',
    dialogue: {
      greeting: 'The world is so vast and beautiful! ...Most of it. Have you seen the fog rolling down from the old lighthouse? I\'ve been watching it for weeks. It\'s not natural fog, traveler. It knows where it\'s going.',
      quests: 'I love exploring new places — and old stories. There\'s a legend about the Coil Light, the lighthouse up the coast. I\'ve chased it through half the village, but Old Ferris up on the dock loft is the only one who\'ll tell it whole — he sailed under that Light for forty years. Climb up and ask him. ...And if you\'re brave, there\'s a path inland that needs walking.',
      complete: 'Wonderful! You\'re becoming quite the explorer yourself. ...You know, explorers find things. Sometimes things that were meant to stay hidden. But that\'s the job, isn\'t it?',
      goodbye: 'Keep exploring! There\'s so much to discover — and some of it, I think, would rather stay buried. ...Be careful past the brambles. The fog there has a weight to it.',
    },
    lines: [
      { afterQuest: 'explore_wickhaven', text: 'You\'ve been over the sea! I sent my cavern and temple notes to Scholar Merrow in Wickhaven. I\'m better at starting expeditions than finishing them, and Merrow has the patience for both.' },
      { afterQuest: 'the_hollow_keeper', text: 'The fog\'s gone from the lighthouse road. I walked it this morning and it was just a road! A beautiful, ordinary road. I think you\'re the reason.' },
      { afterQuest: 'the_fog_inland', text: 'You found Ranger Cole! I\'ve been logging everything: how thick the fog runs by the hour, which way the gulls fly. Did it go cold past the brambles? Cole says it does.' },
      { duringQuest: 'the_fog_inland', text: 'Follow the Drowned Road through Lanternwood. Ranger Cole holds the Brackenmoor gate. You\'ll see his lanterns before you see him.' },
    ],
    availableQuests: ['explorer_first_steps', 'the_fog_inland', 'slime_samples', 'mushroom_forager', 'learn_to_craft', 'craft_your_first_ring', 'weapon_training', 'potion_apprentice', 'jellyfish_study', 'frozen_frontier_prep', 'daily_material_run', 'master_alchemist', 'enchanting_basics'],
  },
  'grant': {
    id: 'grant',
    name: 'Grant the Veteran',
    role: 'trainer' as NPCDataConfig['role'],
    x: 500,
    y: 400,
    sprite: 'npc_warrior',
    dialogue: {
      greeting: 'Ho there, young warrior! You look eager to prove yourself.',
      quests: 'True strength comes from training and perseverance. I have some challenges for you.',
      complete: 'Impressive! You\'ve grown stronger. Keep pushing yourself!',
      goodbye: 'May your blade stay sharp and your spirit strong!',
    },
    availableQuests: ['mighty_warrior'],
    trainerClasses: ['warrior'],
  },
  'guide_rafael': {
    id: 'guide_rafael',
    name: 'Guide Rafael',
    role: 'guide' as NPCDataConfig['role'],
    x: 350,
    y: 400,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'Welcome to Helix2000! I\'m here to guide new adventurers.',
      quests: 'When you reach Level 10, come back to me. I have something special for you.',
      complete: 'You\'ve mastered the basics! The real adventure begins now!',
      goodbye: 'Stay safe out there, and don\'t hesitate to ask for help!',
    },
    availableQuests: ['ready_for_adventure'],
  },
  'guard_captain': {
    id: 'guard_captain',
    name: 'Guard Captain',
    role: 'guard' as NPCDataConfig['role'],
    x: 300,
    y: 400,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Halt! State your business.',
      quests: 'We could use help keeping the peace. Interested?',
      complete: 'Good work. The town is safer thanks to you.',
      goodbye: 'Move along, citizen.',
    },
    lines: [
      { afterQuest: 'dark_forest_expedition', text: 'The forest beyond Lanternwood is over the sea now, and so are its bounties. Warden Halden posts them at Wickhaven\'s gate. When the ferry runs, he\'ll be glad of you.' },
      { afterQuest: 'talk_to_the_guard', text: 'You again. Elowen vouched for you, so I\'ll skip the "halt." The harbor\'s quieter with you around.' },
    ],
    availableQuests: ['keep_the_peace', 'supply_run', 'pig_champion', 'elite_slayer', 'dark_forest_expedition', 'bee_problem', 'boar_hunt', 'forest_patrol', 'elite_slime_threat', 'shadow_menace'],
  },
  'villager_bob': {
    id: 'villager_bob',
    name: 'Villager Bob',
    role: 'civilian' as NPCDataConfig['role'],
    x: 250,
    y: 420,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'Beautiful day, isn\'t it?',
      quests: 'I don\'t have much to offer, but I heard there\'s trouble on Brinebristle Strand.',
      complete: 'Thanks for helping out around here!',
      goodbye: 'Take care!',
    },
    availableQuests: [],
  },
  'merchant_maria': {
    id: 'merchant_maria',
    name: 'Merchant Maria',
    role: 'shop' as NPCDataConfig['role'],
    x: 600,
    y: 450,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Welcome to the village! I sell potions and basics. The field can be rough — stock up.',
      quests: 'I don\'t have any tasks, but Tomas might.',
      complete: 'Thank you!',
      goodbye: 'Stay safe out there.',
    },
    lines: [
      { afterQuest: 'missing_supplies_1', text: 'Pell in Wickhaven was to take those crates off the ice road. Ask him what he\'s heard; the ferry leaves from Tidewend\'s dock.' },
      { afterQuest: 'first_hunt', text: 'Back from the field in one piece! Lark\'s report made the rounds: eight pigs, they\'re saying. Maren will sell you oil in Tidewend, but nobody brews a {item:red_potion} like mine.' },
    ],
    availableQuests: ['missing_supplies_1', 'equipped_for_adventure'],
    shopItems: ['red_potion', 'blue_potion', 'orange_potion', 'power_elixir', 'return_scroll', 'bandage', 'antidote', 'elixir_of_speed', 'elixir_of_fortitude', 'hearth_lantern'],
  },
  'village_elder': {
    id: 'village_elder',
    name: 'Elder Aislin',
    role: 'guide' as NPCDataConfig['role'],
    x: 600,
    y: 400,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'You woke in the Old Vault, didn\'t you? The candles were lit for you. We\'ve been waiting.',
      quests: 'The same carved stones that line the Vault have surfaced ahead in First Field. Recover three and give them to Warden Lark for my courier.',
      complete: 'The field carried the Vault\'s marks east after all. Thank you for following where they led.',
      goodbye: 'Go east with Tomas\'s charge. Warden Lark will receive anything you find.',
    },
    lines: [
      { afterQuest: 'relight_the_coil', text: 'Every candle in the Vault leaned east last night, toward the Coil. I think they knew before the courier did. You did that.' },
      { afterQuest: 'relic_collector', text: 'Lark\'s courier brought me your stones. The marks match the Vault\'s line for line; someone carried them out to that field long before any of us. I\'ll be reading them for weeks.' },
      { duringQuest: 'relic_collector', text: 'The stones look like plain rock until the light catches the carving. Three of them, out in First Field. Warden Lark holds the courier case.' },
      { beforeQuest: 'relic_collector', afterQuest: 'first_hunt', text: 'Tomas tells me the field road is open. If you walk it again, watch the ground. The Vault\'s carved stones have been surfacing out there.' },
    ],
    availableQuests: ['relic_collector'],
  },
  'blacksmith_erik': {
    id: 'blacksmith_erik',
    name: 'Blacksmith Erik',
    role: 'shop' as NPCDataConfig['role'],
    x: 800,
    y: 450,
    sprite: 'npc_blacksmith',
    dialogue: {
      greeting: 'Need some gear? I\'ve got the best iron in town!',
      quests: 'I\'m a blacksmith, not a quest giver. But I can sell you quality equipment!',
      complete: 'Fine choice! That\'ll serve you well.',
      goodbye: 'Stay sharp out there!',
    },
    lines: [
      { afterQuest: 'first_armor', text: 'How\'s that vest sitting? Leather wants a week of wear before it moves with you. Come back when it creaks less.' },
      { duringQuest: 'first_armor', text: 'Put the {item:leather_vest} on and let me see the fit. Can\'t judge armor in a bag.' },
    ],
    availableQuests: ['first_armor', 'prove_yourself'],
    shopItems: ['training_sword', 'apprentice_wand', 'training_bow', 'leather_vest', 'leather_boots', 'iron_helm', 'traveler_gloves', 'copper_ring', 'silver_ring', 'travelers_cloak', 'tidelight_lantern'],
  },
  'arcanist_luna': {
    id: 'arcanist_luna',
    name: 'Arcanist Luna',
    role: 'shop' as NPCDataConfig['role'],
    x: 700,
    y: 450,
    sprite: 'npc_mage',
    dialogue: {
      greeting: 'The arcane arts require proper tools. Let me show you what I have.',
      quests: 'I deal in magical wares, not quests.',
      complete: 'May these items amplify your power!',
      goodbye: 'May the arcane guide you.',
    },
    availableQuests: [],
    shopItems: ['oak_staff', 'blue_potion', 'power_elixir', 'frostweave_robe', 'frostweave_hat', 'ring_of_power'],
  },
  'hunter_kael': {
    id: 'hunter_kael',
    name: 'Hunter Kael',
    role: 'shop' as NPCDataConfig['role'],
    x: 750,
    y: 450,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'A fellow tracker! I\'ve got gear for the wilds.',
      quests: 'The hunt is its own quest. Need supplies?',
      complete: 'Good choices. The forest favors the prepared.',
      goodbye: 'Happy hunting!',
    },
    availableQuests: [],
    shopItems: ['composite_bow', 'red_potion', 'orange_potion', 'hunters_mail', 'boots_of_swiftness'],
  },
  'shadow_dealer': {
    id: 'shadow_dealer',
    name: 'Shadow Dealer',
    role: 'shop' as NPCDataConfig['role'],
    x: 650,
    y: 480,
    sprite: 'npc_rogue',
    dialogue: {
      greeting: '...you didn\'t see me. But since you\'re here, want to buy something?',
      quests: 'I don\'t do favors. I do business.',
      complete: 'Pleasure. Now forget my face.',
      goodbye: 'We never met.',
    },
    availableQuests: [],
    shopItems: ['shadow_dagger', 'orange_potion', 'power_elixir', 'shadow_leather', 'subi_throwing_star'],
  },
  'field_warden': {
    id: 'field_warden',
    name: 'Warden Lark',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 4720,
    y: 1270,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Tomas sent you? Good. The last west fence gave way at dusk, and the herd has been bold ever since.',
      quests: 'Clear the pasture and I will mark the road safe. If Aislin trusted you with relics, I can seal them for her courier.',
      complete: 'Eight down and the survey road still stands. The east gate is yours — Elder Wynn keeps the next hearth.',
      goodbye: 'Keep east. Tidewend\'s lamps are just beyond the gate.',
    },
    lines: [
      { afterQuest: 'relight_the_coil', text: 'The Coil\'s beam reaches the survey post now. First night in a year I could read the ledger without a lamp.' },
      { afterQuest: 'never_where_it_lands', text: 'Maelis wrote me one line about you: "Never where it lands." From Maelis, that is a whole letter.' },
      { afterQuest: 'a_qualified_soul', text: 'So you climbed to the rim and Maelis let you sit. I knew I\'d read you right.' },
      { duringQuest: 'a_qualified_soul', text: 'The old road climbs up through the middle of the field. Follow it to the gate at the top. Maelis keeps watch there and will know my name.' },
      { afterQuest: 'first_hunt', text: 'The west fence held through the night, and the herd keeps to its own side now. Your marks are still on the first page of my ledger.' },
    ],
    availableQuests: ['tidewend_welcome', 'a_qualified_soul'],
  },
  'rim_keeper': {
    id: 'rim_keeper',
    name: 'Maelis of the Rim',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 560,
    y: 650,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'Few climb this high on purpose. The hollow below is loud with things that never had a soul; up here you can still hear yourself think.',
      quests: 'Warden Lark sends me the ones worth teaching. If Lark has not sent you yet, sit a while anyway.',
      complete: 'Remember: move first, think on the way.',
      goodbye: 'Light feet. Down you go.',
    },
    lines: [
      { afterQuest: 'never_where_it_lands', text: 'Your feet are quieter than the day you first climbed up. Good. The hollow will keep testing them.' },
      { duringQuest: 'never_where_it_lands', text: 'The guardian walks its rounds by the old observatory. When its fists rise, be in the air. When it throws, be somewhere else.' },
    ],
    availableQuests: ['never_where_it_lands'],
  },
  'beach_naturalist': {
    id: 'beach_naturalist',
    name: 'Naturalist Nessa',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 1120,
    y: 950,
    sprite: 'npc_scientist',
    dialogue: {
      greeting: 'Mind the tide pools. Half this beach looks harmless until it decides to move.',
      quests: 'The shore herd has been rooting through strange drift. Bring me six clean Pig Feet and I can learn what they have been eating.',
      complete: 'Six good samples. The herd is healthy — restless, but healthy. That is useful news lately.',
      goodbye: 'Enjoy the bright water while it lasts. The island gets quieter inland.',
    },
    lines: [
      { afterQuest: 'pig_feet_collector', text: 'Your samples told me plenty. The herd has been eating drift kelp that only washes in after storms out past the Coil. Something out there is stirring the water.' },
      { duringQuest: 'pig_feet_collector', text: 'Six clean {item:pig_foot} from the shore herd, please. Mind the tide pools while you work.' },
    ],
    availableQuests: ['pig_feet_collector'],
  },
  'elder_wynn': {
    id: 'elder_wynn',
    name: 'Elder Wynn',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 950,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'Welcome to Tidewend, traveler. Quiet little place — or it used to be, before the Coil went dark. The lamps burn late these nights, and the pigs won\'t stay home. You look like you\'ve got good sense. We could use some of that.',
      quests: 'These old bones can\'t chase strays anymore, and the young folk are busy keeping the lanterns lit. Lend a hand and I\'ll see you right — and tell you a thing or two about this island worth knowing.',
      complete: 'Bless you. You\'ve a good heart — the island needs more of those just now. The Coil\'s been dark a long time, but good hearts keep the dark at bay as well as any light.',
      goodbye: 'Mind the fog after dark, won\'t you? It comes in earlier than it used to. ...Used to stay out past the second bell, myself. Not anymore.',
    },
    lines: [
      { afterQuest: 'beyond_the_coil', text: 'Rowan wrote back. Praised the Light, then asked for three more of you. I told Rowan there\'s only the one.' },
      { duringQuest: 'beyond_the_coil', afterQuest: 'relight_the_coil', text: 'Warden Rowan keeps the training quarter, east past the stalls. Tell Rowan I sent you, and that the Coil is lit.' },
      { afterQuest: 'relight_the_coil', text: 'Look at it turning out there. I\'d forgotten how the harbor looks with the beam sweeping over it. There\'s a warm bed in Tidewend for you, any night you want it.' },
      { afterQuest: 'strayed_pigs', text: 'The herd\'s settled since you cleared those snails. Still, they keep their noses turned away from the lighthouse. Animals know.' },
      { duringQuest: 'strayed_pigs', text: 'The snails are out past the windmill, slicking the grass. Six of them gone ought to settle the herd.' },
    ],
    availableQuests: ['strayed_pigs', 'beyond_the_coil'],
  },
  'maren': {
    id: 'maren',
    name: 'Maren the Stallkeep',
    role: 'shop' as NPCDataConfig['role'],
    x: 1000,
    y: 950,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Potions, oil, a warm word — Maren\'s stall has it all. What\'ll it be? ...The fog\'s been bad for business, if I\'m honest. Folk come for lantern oil and potions, not trinkets. But we make do. We always make do.',
      quests: 'If you\'re headed out anyway, I could use a hand keeping the lamps lit. And if you can fish — the dock\'s been too quiet since the fog came. The fish sense something, same as the rest of us.',
      complete: 'Perfect. The lanterns\'ll burn bright tonight, fog or no fog. You\'re a sight better than the last adventurer who came through — charged me for directions, that one.',
      goodbye: 'Come back safe, and come back spending! ...Seriously, though — be careful out there. The path inland isn\'t what it was.',
    },
    lines: [
      { afterQuest: 'relight_the_coil', text: 'Folk are buying trinkets again, first time since the fog! For you, the friends\' price. ...It\'s the same price. But friendlier.' },
      { afterQuest: 'fresh_catch', text: 'The village ate your fish two nights running. Old Ferris swears it\'s the best he\'s had in years, and he\'s never said that about anything.' },
      { afterQuest: 'lantern_oil', text: 'Your {item:lantern_oil} is still burning in the lamps along the quay. Every evening I light them I think: that\'s the adventurer\'s batch.' },
      { duringQuest: 'lantern_oil', text: 'Slime gel renders down beautifully. Twelve {item:slime_gel} and the lamps will last the month.' },
      { duringQuest: 'fresh_catch', text: 'Cast off the dock and bring me five {item:fresh_fish}, whatever\'s still brave enough to bite.' },
    ],
    availableQuests: ['lantern_oil', 'fresh_catch'],
    shopItems: ['red_potion', 'blue_potion', 'return_scroll', 'bandage', 'leather_vest', 'leather_boots', 'lantern_oil'],
  },
  'banker_barnaby': {
    id: 'banker_barnaby',
    name: 'Vaultkeeper Barnaby',
    role: 'banker' as NPCDataConfig['role'],
    x: 1330,
    y: 950,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Welcome to the Tidewend Vault. Your gold and items are secure under our lock and key. What would you like to deposit or withdraw today?',
      quests: 'No vault errands today. Keep your coin safe and don\'t lose your keys in the harbor.',
      complete: 'Your assets remain safely recorded in the Tidewend ledgers.',
      goodbye: 'May your vault stay heavy and your pockets never empty.',
    },
    availableQuests: [],
  },
  'old_ferris': {
    id: 'old_ferris',
    name: 'Old Ferris',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 1900,
    y: 650,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'Mind the ladder, friend — these old knees came up here for the quiet, not the company. ...Hah. Sit anyway. From this loft you can see the whole harbor, and on a clear evening, the Coil Light itself. I sailed under that tower forty years. There\'s not much about it I don\'t know.',
      quests: 'You want the true story of the lighthouse? Not the one the young folk tell — the real one. Sit down, then. It\'s not short, and it doesn\'t end well.',
      complete: 'Now you know what the village won\'t say out loud. If you\'re bound inland — past the brambles, where the fog sits — you go careful. And if you find anything of his up there... anything in his own hand... you bring it to someone who\'ll do right by it. The dead deserve their story told.',
      goodbye: 'Watch the tide on your way down. It comes in faster than it used to. Everything does, these days.',
    },
    lines: [
      { duringQuest: 'explore_wickhaven', text: 'Take the dock gate below me to Tidewend Port. Captain Maris will carry you across; follow the town gate from Wickhaven Port to Isolde\'s hearth. Tell her the old man sent you, and that the Coil burns.' },
      { afterQuest: 'the_hollow_keeper', text: 'I watched the fog lift from up here, all at once, like a held breath let go. You found him, didn\'t you. ...Thank you. He\'d have hated being a ghost story.' },
      { afterQuest: 'keepers_journal', text: 'Cole brought me the pages you found. That\'s his hand, sure as the tide. Forty years I knew him and never saw him write a sad word, until those.' },
      { afterQuest: 'keepers_legend', text: 'Back for more of an old man\'s stories? I\'ve told you the one that matters. Now I just watch the tower and wait for it to light.' },
    ],
    availableQuests: ['keepers_legend', 'explore_wickhaven'],
  },
  'captain_elowen': {
    id: 'captain_elowen',
    name: 'Captain Elowen',
    role: 'trainer' as NPCDataConfig['role'],
    x: 2700,
    y: 950,
    sprite: 'npc_warrior',
    dialogue: {
      greeting: 'Steel is loud, but discipline is quiet. I train warriors to hold their ground and lead the charge when the fog starts whispering.',
      quests: 'No errands today. Show me a steady stance, a clean recovery, and the sense to protect the person beside you.',
      complete: 'Good. Strength is what remains after panic spends itself.',
      goodbye: 'Keep your guard high past the east watch. Brackenmoor punishes swagger.',
    },
    lines: [
      { afterQuest: 'relight_the_coil', text: 'The Light\'s back, and so is the swagger in my recruits. Come spar with them sometime and remind them what earned it.' },
      { afterQuest: 'mighty_warrior', text: 'Level five, and you still stand like you mean it. Most lose their stance once they get strong. Don\'t.' },
      { duringQuest: 'talk_to_the_guard', text: 'The Guard Captain keeps the harbor watch. Go on, and tell the Captain I said you\'re steady.' },
    ],
    availableQuests: ['talk_to_the_guard', 'mighty_warrior', 'veteran_hunter', 'gear_up', 'ready_for_adventure', 'legend_of_helix'],
    trainerClasses: ['warrior'],
  },
  'justiciar_kareth': {
    id: 'justiciar_kareth',
    name: 'Justiciar Kareth',
    role: 'trainer' as NPCDataConfig['role'],
    x: 2540,
    y: 950,
    sprite: 'npc_warrior',
    dialogue: {
      greeting: 'The Light does not promise victory, recruit; it promises purpose. Paladins stand where the shadows fall deepest so others may walk in the morning.',
      quests: 'Devotion is proven with every strike and every blessing. Keep your faith unyielding and your hammer true.',
      complete: 'The blessing takes hold. Go forward with honor, and let the darkness fear your stride.',
      goodbye: 'Walk in the Light, and never lower your shield against the creeping dark.',
    },
    availableQuests: [],
    trainerClasses: ['paladin'],
  },
  'arcanist_vale': {
    id: 'arcanist_vale',
    name: 'Arcanist Vale',
    role: 'trainer' as NPCDataConfig['role'],
    x: 2940,
    y: 950,
    sprite: 'npc_mage',
    dialogue: {
      greeting: 'The harbor lamps bend toward the Coil after midnight. As mages, we harness the weave of arcane energy while others merely watch it flicker.',
      quests: 'Power without control only gives the dark a brighter doorway. We begin with breath, focus, and one spell cast correctly.',
      complete: 'Better. The spell answered you instead of the other way around.',
      goodbye: 'If the fog speaks in a familiar voice, do not answer. Come find me.',
    },
    availableQuests: [],
    trainerClasses: ['mage'],
  },
  'warden_rowan': {
    id: 'warden_rowan',
    name: 'Warden Rowan',
    role: 'trainer' as NPCDataConfig['role'],
    x: 3200,
    y: 950,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'A hunter survives by reading what the wild refuses to say. Tonight the gulls are inland and the harbor hounds won\'t face east.',
      quests: 'Learn the wind, the ground, and your quarry\'s scent before you loose an arrow.',
      complete: 'There. You listened before you moved. The island rewards that.',
      goodbye: 'Watch the treeline, not the path. The path is where it wants you looking.',
    },
    lines: [
      { afterQuest: 'corruption_spreads_1', text: 'Halden keeps Wickhaven\'s gate and the forest road behind it. My letter went over on the morning boat: he knows you\'re coming. Take the dock gate to Tidewend Port and ask Captain Maris for passage.' },
      { afterQuest: 'beyond_the_coil', text: 'The one who lit the Coil, in my quarter. Elder Wynn\'s letter was mostly underlining. The corruption is spreading on the mainland. When you\'re ready, so are we.' },
    ],
    availableQuests: ['corruption_spreads_1'],
    trainerClasses: ['hunter'],
  },
  'druid_elora': {
    id: 'druid_elora',
    name: 'Druid Elora',
    role: 'trainer' as NPCDataConfig['role'],
    x: 3530,
    y: 950,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'The roots run deeper than the harbor pilings, drink of the salt, and still bloom green. The cycle of growth and decay yields to those who listen.',
      quests: 'To heal the grove, you must understand both the budding leaf and the rot beneath the bark. Balance is everything.',
      complete: 'The wild whispers in your blood now. Guard the balance, and nature will lend you her vigor.',
      goodbye: 'Walk softly upon the loam. The earth remembers every footprint.',
    },
    availableQuests: [],
    trainerClasses: ['druid'],
  },
  'whisper_tamsin': {
    id: 'whisper_tamsin',
    name: 'Whisper Tamsin',
    role: 'trainer' as NPCDataConfig['role'],
    x: 3420,
    y: 950,
    sprite: 'npc_rogue',
    dialogue: {
      greeting: 'If you saw me before I spoke, I was being polite. Rogues learn here after the market closes and before the watch asks questions.',
      quests: 'A good shadow is patient. A great one knows when stepping into the lantern light is the better disguise.',
      complete: 'Quieter. Not quiet — quieter. That\'s enough to keep breathing.',
      goodbye: 'We didn\'t meet. If we did, I was taller.',
    },
    availableQuests: [],
    trainerClasses: ['rogue'],
  },
  'stormspeaker_iona': {
    id: 'stormspeaker_iona',
    name: 'Stormspeaker Iona',
    role: 'trainer' as NPCDataConfig['role'],
    x: 3650,
    y: 950,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'The tide keeps three rhythms tonight, and none belong to the moon. Shamans feel the wrongness first. Everyone else hears it when the windows shake.',
      quests: 'Do not command the storm. Introduce yourself, listen to its answer, and ask what price the lightning expects.',
      complete: 'The current knows your name now. Be sure it learns your manners too.',
      goodbye: 'Walk softly on the docks. The water below them is listening.',
    },
    availableQuests: [],
    trainerClasses: ['shaman'],
  },
  'sister_vael': {
    id: 'sister_vael',
    name: 'Sister Vael',
    role: 'trainer' as NPCDataConfig['role'],
    x: 3780,
    y: 950,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'Even the darkest harbor cannot drown a steady prayer. I teach the faithful how to mend what has been broken and shelter those whom the fog would claim.',
      quests: 'Healing demands quiet resolve. Center your heart, listen for the breath of grace, and offer warmth to the weary.',
      complete: 'Your prayer is heard. Carry its soothing light to those who walk through shadow.',
      goodbye: 'May the dawn find you whole, traveler. Tend to the suffering wherever you go.',
    },
    availableQuests: [],
    trainerClasses: ['priest'],
  },
  'hexbinder_kane': {
    id: 'hexbinder_kane',
    name: 'Hexbinder Kane',
    role: 'trainer' as NPCDataConfig['role'],
    x: 4250,
    y: 950,
    sprite: 'npc_mage',
    dialogue: {
      greeting: 'The others avert their eyes from the abyss; we bind it to our will. Speak softly — the shadows in Tidewend have ears, and they answer to me.',
      quests: 'Power is not given, it is seized and forged through pacts. Learn to wield torment without letting it consume you.',
      complete: 'The pact is sealed. Feed the flame with agony, and watch your foes wither.',
      goodbye: 'Keep your curses subtle until you intend to strike. Shadow favors the patient.',
    },
    availableQuests: [],
    trainerClasses: ['warlock'],
  },
  'ranger_cole': {
    id: 'ranger_cole',
    name: 'Ranger Cole',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 700,
    y: 1150,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Far enough, friend. The wood\'s gone wrong past here — colder every night, and things in it that shouldn\'t be. I\'ve held this path three years now. Used to be boars and the odd wolf. Now? ...Well. You\'ll see.',
      quests: 'I can\'t hold this path alone. If you\'ve the nerve, there\'s grim work that needs doing — and there\'s journal pages, scattered in the bracken. The keeper\'s own hand. If you find them, bring them to me. The dead deserve their story told, even when the living don\'t want to hear it.',
      complete: '...Good. That\'s one less horror between us and the dark. Steady on. And if you\'ve pages for me — I\'ll take them. Every one.',
      goodbye: 'Keep your lantern close. The fog hates the light. ...You know, I used to hunt by moonlight. Now I don\'t go out without a flame. The dark here has teeth.',
    },
    lines: [
      { afterQuest: 'relight_the_coil', text: 'The beam crosses the bracken every few breaths now. First time in three years I\'ve held this path without a lantern in my fist. ...I still carry it. Old habits.' },
      { afterQuest: 'the_hollow_keeper', text: 'You came back up from the Undercroft. I wasn\'t sure anyone would. The wood\'s warmer already. I can hear birds again.' },
      { afterQuest: 'keepers_journal', text: 'I keep the keeper\'s pages wrapped in oilcloth. Someone ought to read them aloud in Tidewend one day. Maybe you.' },
      { afterQuest: 'whats_in_the_mist', text: 'Whatever\'s calling those things, it\'s down in the lighthouse undercroft. We both know what that means.' },
      { afterQuest: 'clear_the_brambleway', text: 'The brambleway\'s still clear. The boars keep back now. It\'s the fog itself I don\'t trust.' },
      { afterQuest: 'the_fog_inland', text: 'Sophia\'s friend, aren\'t you? She sends me letters about the fog. Good questions, every one. I\'ve no answers for her.' },
    ],
    availableQuests: ['clear_the_brambleway', 'whats_in_the_mist', 'the_hollow_keeper', 'relight_the_coil', 'keepers_journal', 'keep_the_coil_lit'],
  },
  'pip_reedwhistle': {
    id: 'pip_reedwhistle',
    name: 'Pip Reedwhistle',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 1050,
    y: 1470,
    sprite: 'npc_civilian',
    dialogue: {
      greeting: 'You\'re going down the long road, aren\'t you? Mallow says it feels shorter if you know where the quiet bits are.',
      quests: 'He\'s in our hiding place. Look for a little lantern with three round windows and a crooked blue ribbon. It hangs by the door of an old boathouse gone all green, under the broken arch on the road. If the ribbon wiggles, have a closer look.',
      complete: 'Good! Oh—and you don\'t have to come all the way back to tell me. Mallow can show you the nice way to leave.',
      goodbye: 'Keep an eye on the reeds! The road is full of quiet corners if you look.',
    },
    lines: [
      { afterQuest: 'somewhere_the_road_is_quiet', text: 'He taught you! I knew he would. Now we both know where to find you: probably two steps farther along than we expected.' },
      { duringQuest: 'somewhere_the_road_is_quiet', text: 'Three round windows. Crooked blue ribbon. The reeds on the Drowned Road are hiding something much smaller than a monster. Stay with the road until you find an old boathouse so overgrown it looks like a hedge, under a broken stone arch. Our lantern hangs by its door.' },
    ],
    availableQuests: ['somewhere_the_road_is_quiet'],
  },
  'mallow_wick': {
    id: 'mallow_wick',
    name: 'Mallow Wick',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 520,
    y: 670,
    sprite: 'npc_scholar',
    dialogue: {
      greeting: 'Come in. The road is loud up there. Down here you can hear the little things.',
      quests: 'Souls have been passing this little window for a long time. Some stomp. Some float. One used to hop as if the road had puddles only they could see. I watched their feet until I understood the small bit: don\'t hurry the whole road. Just borrow the next step.',
      complete: 'There. Two little steps for when one feels too ordinary. The stones are flat if you want to try. The road will still be there afterward.',
      goodbye: 'The opening there takes you back to the same bit of road. Try not to skip past anyone saying hello.',
    },
    lines: [
      { afterQuest: 'somewhere_the_road_is_quiet', text: 'Two little steps for when one feels too ordinary. The stones are flat if you want to try. The road will still be there afterward. Cole is farther along the road—you can borrow a few steps on the way.' },
      { duringQuest: 'somewhere_the_road_is_quiet', text: 'Pip told you about the ribbon, didn\'t he? He is very good at keeping secrets that have directions. Come in. The road is loud up there; down here you can hear the little things. When you\'re ready, I\'ll teach you how to borrow the next step.' },
    ],
    availableQuests: ['somewhere_the_road_is_quiet'],
  },
  'wickhaven_banker': {
    id: 'wickhaven_banker',
    name: 'Vaultkeeper Hesk',
    role: 'banker' as NPCDataConfig['role'],
    x: 250,
    y: 950,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Off the ferry and straight to the vault — sensible. Whatever you leave with me stays where you left it. Tidewend\'s ledgers and mine agree to the copper; we row the books across every week.',
      quests: 'I count coin, I don\'t hand out errands. Ask the warden by the gate — he always has something that needs killing.',
      complete: 'Recorded, sealed, and under the lamp. Nobody touches a vault in Wickhaven in the dark.',
      goodbye: 'Mind your purse on the roads. The old world is not as polite as the harbor.',
    },
    availableQuests: [],
  },
  'wickhaven_grocer': {
    id: 'wickhaven_grocer',
    name: 'Provisioner Pell',
    role: 'shop' as NPCDataConfig['role'],
    x: 470,
    y: 950,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'Fresh off the boat? You\'ll want potions before you want anything else, trust me. The roads out of here go cold, hot, dark and deep, and I sell for all four.',
      quests: 'One job, and it\'s a sore one: Maria\'s shipment from The First Hearth never reached my stores. Otherwise, buy a Return Scroll — read it anywhere and you\'ll wake up by our hearth. Cheapest way home there is.',
      complete: 'Pleasure doing business. Come back with an empty bag and a full purse.',
      goodbye: 'Drink before you need to, not after. Folk forget that part.',
    },
    lines: [
      { duringQuest: 'missing_supplies_2', text: 'Maria\'s crates were crossing the Frozen Lake when the weather turned. Follow the Dark Forest road through Deep Woods to the Abandoned Camp, then take its lake trail. Look along the shore.' },
      { afterQuest: 'frozen_mystery_4', text: 'Word is the lake\'s thawing. Ice prices will crash, and I just bought a cellar full. ...Still, better that than whatever was living under it.' },
    ],
    availableQuests: ['missing_supplies_2', 'missing_supplies_3', 'missing_supplies_4'],
    shopItems: ['red_potion', 'orange_potion', 'blue_potion', 'power_elixir', 'bandage', 'antidote', 'return_scroll', 'elixir_of_speed', 'elixir_of_fortitude'],
  },
  'wickhaven_smith': {
    id: 'wickhaven_smith',
    name: 'Smith Brannoch',
    role: 'shop' as NPCDataConfig['role'],
    x: 690,
    y: 950,
    sprite: 'npc_blacksmith',
    dialogue: {
      greeting: 'Harbor iron won\'t last you past the gate. Mine will. Steel, mail, a staff with real runes on it — pick what your arm can carry.',
      quests: 'I hammer, I don\'t wander. If you want work, the warden\'s by the gate and the scholar\'s by the well.',
      complete: 'Good fit. Bring it back dented and I\'ll know you\'ve been somewhere worth going.',
      goodbye: 'Keep your edge oiled. The lake eats steel.',
    },
    lines: [
      { afterQuest: 'ancient_temple_4', text: 'You put down a Void Wraith in that temple? Show me the blade. ...Barely a nick. Either you\'re good or I am. Let\'s say both.' },
    ],
    availableQuests: [],
    shopItems: ['steel_sword', 'ice_staff', 'hunter_bow', 'mage_robe', 'iron_platemail', 'copper_chain_belt', 'copper_chain_boots', 'steel_warhammer', 'tribal_spear', 'mail_helm', 'mail_chest', 'mail_legs', 'mail_boots', 'runed_staff', 'hunters_longbow', 'steel_glaive', 'enchanted_silk_robes'],
  },
  'wickhaven_lamplighter': {
    id: 'wickhaven_lamplighter',
    name: 'Lamplighter Isolde',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 940,
    y: 950,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'Welcome to Wickhaven. Every lamp in this town was lit from the Coil, once — when it went dark, so did we. Warm yourself at the hearth. When you need a way back from the roads, use a Return Scroll and follow our lamps home.',
      quests: 'My lamps need shades that won\'t crack in the cold, and nothing holds a flame like an Ice Drake\'s scale. Follow the Dark Forest road through the Deep Woods to the Abandoned Camp. Its lake trail leads to the Frozen Lake.',
      complete: 'Look at that glow through the scale — blue as the lake. The square will be bright tonight because of you.',
      goodbye: 'Go on, then. Follow the lanterns out, and follow them home.',
    },
    lines: [
      { afterQuest: 'explore_wickhaven', text: 'You came on the first ferry since the fog. The square\'s been talking of nothing else. Stay a while, the hearth\'s never out.' },
      { duringQuest: 'explore_wickhaven', text: 'You\'re off the ferry? Then you\'ve found me. Old Ferris sends word with every boat that matters, and he said one was coming.' },
    ],
    availableQuests: ['drake_scale_collector', 'scale_collector', 'daily_frozen_harvest', 'phoenix_feather_quest'],
  },
  'wickhaven_scholar': {
    id: 'wickhaven_scholar',
    name: 'Scholar Merrow',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 1160,
    y: 950,
    sprite: 'npc_scientist',
    dialogue: {
      greeting: 'Ah, a pair of legs! Mine are for reading. The caverns grow living crystal, the temple\'s walls are written in a script nobody living can read, and the ruins under the sea are older than both. I need someone to go and look.',
      quests: 'Bring me pieces, readings, anything. The forest road runs through Deep Woods, the Abandoned Camp and the Old Grove before reaching the Crystal Caverns; a passage beyond them reaches the Ancient Temple. A separate passage leads to the Sunken Ruins. Take the temple road before you brave those depths.',
      complete: 'Marvellous. Marvellous! This changes three of my chapters and ruins a fourth.',
      goodbye: 'Take notes! ...No, you won\'t. Just remember what you saw, then.',
    },
    lines: [
      { afterQuest: 'ancient_temple_5', text: 'The artifact hums when the lamps are lit. I\'ve stopped sleeping in the same room as it. Worth it.' },
      { afterQuest: 'crystal_research', text: 'Living gemstone. It grows like coral and hums like a struck glass. I\'ve filled a notebook and understood a page of it.' },
    ],
    availableQuests: ['crystal_research', 'crystal_expedition', 'crystal_expedition_1', 'crystal_expedition_2', 'crystal_expedition_3', 'crystal_expedition_4', 'ice_fishing', 'crystal_hunter', 'material_collector', 'ultimate_crafter', 'fire_crystal_quest', 'frost_shard_quest', 'void_essence_quest', 'temple_discovery', 'ancient_temple_1', 'altar_mystery', 'shadow_investigation', 'sunken_discovery', 'ancient_power'],
  },
  'wickhaven_warden': {
    id: 'wickhaven_warden',
    name: 'Warden Halden',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 1380,
    y: 950,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'One inland road leaves Wickhaven: the Dark Forest. Follow it through the Deep Woods to the Abandoned Camp. The lake trail leaves from the camp; the Old Grove leads onward to the Crystal Caverns. Read the trail signs. I\'m tired of fishing heroes out of the lake.',
      quests: 'Bounties are on the board: golems in the caverns, stalkers in the temple, the volcano\'s vermin, the ruins\' dead armour. And something\'s wrong on the Frozen Lake. Bodies, frozen where they stood.',
      complete: 'Paid in full. The roads are safer for a day. That\'s the job: one day at a time.',
      goodbye: 'Follow the lanterns home. If you can\'t see one, you\'ve gone too far.',
    },
    lines: [
      { duringQuest: 'corruption_spreads_2', text: 'Rowan\'s letter said you\'d come. Trolls on the forest road, and worse behind them. Our east gate opens onto that road.' },
      { afterQuest: 'frozen_mystery_4', text: 'The Frost Elemental, gone. I\'ve had that lake on my reports for eleven years. I\'ll have to find something else to complain about.' },
      { duringQuest: 'frozen_mystery_1', text: 'Take the east gate through the Dark Forest and Deep Woods to the Abandoned Camp, then follow its lake trail. Look along the shore, not out on the ice. The ice is where the things that did it live.' },
      { duringQuest: 'forest_road_dispatch', text: 'Follow the Dark Forest road into the Deep Woods. Scout Iven waits at its western lookout and has supplies for you. The camp and old grove lie farther on.' },
    ],
    availableQuests: ['forest_road_dispatch', 'frozen_mystery_1', 'corruption_spreads_2', 'corruption_spreads_3', 'corruption_spreads_4', 'corruption_spreads_5', 'troll_trouble', 'frozen_frontier', 'ice_drake_menace', 'cavern_expedition_prep', 'golem_threat', 'cursed_patrol', 'temple_guardians', 'temple_treasures', 'volcano_ascent', 'fire_and_fury', 'void_hunter', 'ruin_guardians', 'void_containment', 'phoenix_hunt', 'champion_of_helix', 'daily_elite_bounty', 'daily_temple_clear', 'daily_volcano_patrol', 'daily_ruin_sweep'],
  },
  'tidewend_ferrymaster': {
    id: 'tidewend_ferrymaster',
    name: 'Captain Maris',
    role: 'ferrymaster' as NPCDataConfig['role'],
    x: 720,
    y: 950,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'Welcome aboard. The lamps mark our landing in Wickhaven; shall we make the crossing?',
      quests: 'The town gates are just along the quay. Follow the lamps.',
      complete: 'Lines cast off. Safe passage to you.',
      goodbye: 'Fair winds, traveller.',
    },
    availableQuests: [],
  },
  'wickhaven_ferrymaster': {
    id: 'wickhaven_ferrymaster',
    name: 'Captain Alder',
    role: 'ferrymaster' as NPCDataConfig['role'],
    x: 720,
    y: 950,
    sprite: 'npc_villager',
    dialogue: {
      greeting: 'Welcome aboard. The lamps mark our landing in Tidewend; shall we make the crossing?',
      quests: 'The town gates are just along the quay. Follow the lamps.',
      complete: 'Lines cast off. Safe passage to you.',
      goodbye: 'Fair winds, traveller.',
    },
    availableQuests: [],
  },
  'glowspore_orla': {
    id: 'glowspore_orla',
    name: 'Mycologist Orla',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 250,
    y: 1250,
    sprite: 'npc_scientist',
    dialogue: {
      greeting: 'Easy with the lamp. The caps copy whatever light they see, and yours has already woken three of them.',
      quests: 'I left my field notes on the western canopy. Recover them, quiet the Keeper in the far hollow, and bring me one heartseed from the high crown. We can grow a light that will travel with you.',
      complete: 'There. A living glow, sealed in glass. It will follow you into places where an ordinary flame would rather go out.',
      goodbye: 'Cloud rising? Step clear. Stones stirring? Leave the marked spot. Then come back and tell me what you found.',
    },
    lines: [
      { duringQuest: 'glowspore_light_that_lasts', text: 'My notes are on the western canopy. The Keeper has rooted itself into the far pocket, below the heartseed crown. Bring both finds back after you have quieted it.' },
      { afterQuest: 'glowspore_light_that_lasts', text: 'Your Sporeglass Lantern is glowing steadily. The colony remembers gentle hands, eventually.' },
    ],
    availableQuests: ['glowspore_light_that_lasts'],
  },
  'moonfen_reedwarden': {
    id: 'moonfen_reedwarden',
    name: 'Reedwarden Tamsin',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 520,
    y: 710,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'The pasture looks empty from this rim. It isn\'t. Someone counted every animal through the floods, and a light still burns at the far watchtower. I\'d like to know who kept it.',
      quests: 'Follow the keeper\'s records: a tally by the Reedwater stilts, a ledger in the grange loft, and a last letter at the beacon\'s crow nest. Bellkeeper Orren waits on the watchtower below it. Take all three to him, not back to me.',
      complete: 'Records tell you where to look. People tell you why it mattered.',
      goodbye: 'The watchtower has a return trail to the Outskirts. You needn\'t cross the whole fen twice.',
    },
    lines: [
      { duringQuest: 'moonfen_last_lantern', text: 'Three different records: Reedwater, the grange, then the far beacon. Orren can put the story together. Hand them to him at the watchtower; the return trail is beside his lantern.' },
      { afterQuest: 'moonfen_last_lantern', text: 'Orren sent you back with a Fenwatch Lantern? Then he has company in the dark again. Keep it lit. That is how this pasture remembers people.' },
    ],
    availableQuests: ['moonfen_last_lantern'],
  },
  'moonfen_beacon_keeper': {
    id: 'moonfen_beacon_keeper',
    name: 'Bellkeeper Orren',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 9560,
    y: 1240,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'You came all this way. Most lights are meant to draw people closer; this one was meant to show the herd a dry place to stand. I stayed in case anyone else followed it.',
      quests: 'Tamsin keeps the western rim. If she sent you for the records, bring me the Reedwater Tally, the Herdkeeper\'s Ledger, and the Last Watch Letter. The last is just above us.',
      complete: 'Nobody abandoned the pasture. They kept moving until the small ones were safe, then left a light for those still coming. Carry one of ours.',
      goodbye: 'The lantern to my right marks the way back to the Tidewend Outskirts. It is open whether you found every page or simply need a rest.',
    },
    lines: [
      { duringQuest: 'moonfen_last_lantern', text: 'The tally names the herd, the ledger follows the families, and the letter tells you who stayed. Bring all three and I\'ll give you a Fenwatch Lantern for your own road.' },
      { afterQuest: 'moonfen_last_lantern', text: 'That green-gold light suits you. Put the Fenwatch Lantern in your Lantern slot when the road grows dark. Someone will be glad to see it coming.' },
    ],
    availableQuests: [],
  },
  'forest_scout_iven': {
    id: 'forest_scout_iven',
    name: 'Scout Iven',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 384,
    y: 1614,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Halden still calls this the foresters\' road. Most of the foresters left when the roots began moving. Hetta went back for the tools. She says a road is only abandoned when nobody intends to mend it.',
      quests: 'Two lone trolls hold the root crossing farther east. Clear them, then continue into the abandoned camp. Hetta\'s tool roll is on the store loft above the loaded handcart. Take it to her in the salvage yard beyond the loft; you needn\'t bring it back to me.',
      complete: 'Good. Take these supplies before you follow the road. Hetta will be glad to hear a voice that isn\'t a troll.',
      goodbye: 'The open road continues through the camp into the old grove. The lake trail leaves the camp halfway through.',
    },
    lines: [
      { duringQuest: 'forest_road_dispatch', text: 'Halden sent you? I can use the help. Speak to me before you take on the root crossing; I have supplies for the journey.' },
      { afterQuest: 'forest_camp_recovery', text: 'You found Hetta and her tools. That\'s a better message than another list of things we\'ve lost.' },
    ],
    availableQuests: ['forest_camp_recovery'],
  },
  'forest_sawkeeper_hetta': {
    id: 'forest_sawkeeper_hetta',
    name: 'Sawkeeper Hetta',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 7168,
    y: 1678,
    sprite: 'npc_blacksmith',
    dialogue: {
      greeting: 'Everyone thinks I\'m here to cut the old trees. I\'m here for a pruning saw and a grafting knife. Those tools kept this grove alive before the camp ever sold a plank.',
      quests: 'Beyond the camp, the Hollow Crown has taken root in the old grove\'s heart court. Its dead branches strike the ground before its roots move. Watch that warning and jump the shockwave. Defeat it, then speak to Grovekeeper Senn on the road beyond the court.',
      complete: 'My roll, and every blade still wrapped. Thank you. These potions are yours; a long road shouldn\'t leave you choosing between your last meal and your last medicine.',
      goodbye: 'Senn keeps the eastern road open. The cavern entrance is farther on. Neither road waits for my permission.',
    },
    lines: [
      { duringQuest: 'forest_camp_recovery', text: 'The tool roll is still on the store loft above the loaded handcart to the west. Iven also asked you to clear two Forest Trolls. Bring the roll here when you\'ve done both.' },
      { afterQuest: 'forest_old_root', text: 'Senn gave you a forester\'s mantle? Wear it well. The people who made those knew how to shelter a traveller without cutting down every tree around them.' },
    ],
    availableQuests: ['forest_old_root'],
  },
  'forest_grovekeeper_senn': {
    id: 'forest_grovekeeper_senn',
    name: 'Grovekeeper Senn',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 7360,
    y: 1614,
    sprite: 'npc_guide',
    dialogue: {
      greeting: 'The old foresters tended what they cut. Then the camp fell silent, and the Hollow Crown began feeding on the roots they left behind. I stay because silence is a poor keeper.',
      quests: 'Hetta knows which tools to bring back. If she sent you after the Hollow Crown, tell me when you\'ve defeated it. Ordinary Dark Stumps are another trouble; they are not this guardian.',
      complete: 'You gave the grove a breathing space. I cannot promise the Crown will never stir again. I can promise that someone will be here to meet the next traveller. Take this mantle; it belonged to someone who kept that promise.',
      goodbye: 'The near trail returns to the forest road. Farther east lie the Crystal Caverns. Your Return Scrolls follow your last town\'s lamps when you need a proper rest.',
    },
    lines: [
      { duringQuest: 'forest_old_root', text: 'The Hollow Crown stands in the heart court to the west. Watch for the ground strike. When it falls, come back to me for your mantle.' },
      { afterQuest: 'forest_old_root', text: 'The mantle fits. Whatever returns to that court, you will remember how to face it. There is no shame in taking the road home first.' },
    ],
    availableQuests: [],
  },
  'savanna_outrider': {
    id: 'savanna_outrider',
    name: 'Outrider Kestrel',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Wickhaven sent me to see whether the south road can carry wagons. The grass says yes. Everything living in it says no.',
      quests: 'Start with the striders by the road; they kick at anything on two legs. When you\'re ready for worse, the Tuskmire camp sits in the middle of the plain, and the Stripe-runner herds roam past it to the east.',
      complete: 'That\'s one more stretch of road I can mark as passable. Here, you\'ve earned this.',
      goodbye: 'The portal behind me goes straight back to Wickhaven. Use it if the grass gets the better of you.',
    },
    lines: [
      { duringQuest: 'savanna_tuskmire_camp', text: 'The Tuskmire camp is past the striders, under the big termite mound. One of them always keeps watch from the top.' },
      { afterQuest: 'savanna_stripe_runners', text: 'Striders, raiders and the herds. I\'ll tell Wickhaven the south road is open, as long as people walk it armed.' },
    ],
    availableQuests: ['savanna_longleg_hunt', 'savanna_strider_feathers', 'savanna_tuskmire_camp', 'savanna_stripe_runners', 'savanna_tuskmire_warchief'],
  },
  'savanna_trader': {
    id: 'savanna_trader',
    name: 'Camp Trader Wren',
    role: 'shop' as NPCDataConfig['role'],
    x: 360,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Last stall before the grass gets tall. Potions, rations, rope, ore. Whatever you forgot.',
      quests: 'No errands from me. Kestrel\'s the one with work; I just keep you stocked for it.',
      complete: 'Good trade. Come back when the bag\'s empty.',
      goodbye: 'Watch the striders. They kick.',
    },
    availableQuests: [],
    shopItems: ['health_potion', 'minor_healing_potion', 'mana_potion', 'energy_potion', 'drink_hearthside_mild', 'drink_cellar_milk', 'flash_powder', 'scroll_small_tree', 'scroll_boulder', 'fishing_pole_basic', 'reagent_fish_oil', 'calmbloom', 'tiger_meat', 'bronze_bar', 'raw_iron_ore', 'weak_flux', 'glyphcloth', 'spellweave_cloth', 'kobold_cloth_scrap', 'dream_dust', 'illusion_dust', 'arcane_charges'],
  },
  'swamp_bogwarden': {
    id: 'swamp_bogwarden',
    name: 'Bogwarden Tilly',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Mind the boards. Anything that isn\'t a board is either mud, a Gilling or something with teeth.',
      quests: 'The Gillings have a camp on the roots just past me, and they raid every net on the water. Past them, the Murkwater Snapjaws sun themselves on the old boardwalk. Clear the camp first.',
      complete: 'Good. That\'s a little less of the swamp trying to eat my road. Take this.',
      goodbye: 'The savanna\'s back through the portal behind me, if you need dry ground.',
    },
    lines: [
      { duringQuest: 'swamp_snapjaw_road', text: 'The Snapjaws lie still until you\'re close enough to touch. Then they don\'t.' },
      { afterQuest: 'swamp_snapjaw_road', text: 'Boardwalk\'s clear for now. I\'ll send word to Kestrel on the savanna that the Lowlands road reaches the swamp.' },
      { duringQuest: 'swamp_bogchief', text: 'Sloughmaw\'s the one with the shells on his head. If it\'s got a trident, it\'s him.' },
    ],
    availableQuests: ['swamp_gilling_camp', 'swamp_gilling_raids', 'swamp_snapjaw_road', 'swamp_toadstool_creepers', 'swamp_bogchief'],
  },
  'swamp_trader': {
    id: 'swamp_trader',
    name: 'Stilt-trader Pell',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Dry goods, on dry boards. Potions, bandages and bread, and none of it smells of the swamp. Yet.',
      quests: 'Tilly\'s the one with work. I just keep the mud out of your pack.',
      complete: 'Sold. Mind your step off the boards.',
      goodbye: 'The Gillings like shiny things. Keep your coin out of sight.',
    },
    availableQuests: [],
    shopItems: ['greater_health_potion', 'orange_potion', 'greater_mana_potion', 'antidote', 'bandage', 'elixir_of_speed', 'return_scroll', 'food_rye_bread'],
  },
  'shore_tidewatcher': {
    id: 'shore_tidewatcher',
    name: 'Tidewatcher Marlo',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Sun\'s out, tide\'s out. Watch where you step: the sand bites here.',
      quests: 'Sandstingers nest all along the beach, and Basilisks have the rocks past the tide pools. Clear the stingers first, then we\'ll talk about the stone-eyes.',
      complete: 'That\'s more beach we can walk on. Take this, and shake the sand out of your boots.',
      goodbye: 'The swamp\'s back through the portal behind me, if you miss the mud.',
    },
    lines: [
      { duringQuest: 'shore_basilisk_rocks', text: 'Don\'t look a Basilisk in the eye. Look at its feet. Feet can\'t turn you to stone.' },
      { afterQuest: 'shore_basilisk_brood', text: 'The whole shore\'s clear. Kestrel and Tilly will hear the Lowlands road runs all the way to the sea.' },
      { duringQuest: 'shore_leviathan', text: 'Don\'t try to outswim it. Stay on the sand and let it come to you.' },
    ],
    availableQuests: ['shore_sandstinger_nests', 'shore_sandstinger_sweep', 'shore_basilisk_rocks', 'shore_basilisk_brood', 'shore_siren_song', 'shore_leviathan'],
  },
  'shore_trader': {
    id: 'shore_trader',
    name: 'Tide Merchant Dulcie',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Fresh off the boat, or near enough. Potions, bandages, stew. Buy now; the tide takes the rest.',
      quests: 'Marlo watches the shore and hands out the work. I just watch the stall.',
      complete: 'Good. Keep your feet dry.',
      goodbye: 'Don\'t look the Basilisks in the eye, and don\'t listen to the singing.',
    },
    availableQuests: [],
    shopItems: ['potion_major_healing', 'greater_health_potion', 'greater_mana_potion', 'elixir_of_fortitude', 'antidote', 'silk_bandage', 'spiced_bear_stew', 'return_scroll'],
  },
  'moor_warden': {
    id: 'moor_warden',
    name: 'Moorwarden Brannock',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Keep your purse inside your coat. The wind isn\'t the only thing on this moor that takes what it likes.',
      quests: 'The Windmoor Brigands own this road. Cutpurses on the heath, thugs round a peat fire, smugglers in the cuttings and, at the far end, Captain Morrow Vane. Start with the cutpurses.',
      complete: 'One less Brigand on my road. Take this for your trouble.',
      goodbye: 'Wickhaven\'s back through the portal behind me, if the wind gets to you.',
    },
    lines: [
      { duringQuest: 'moor_captain_vane', text: 'The Captain fights like someone who has never lost. Make that the last thing they get wrong.' },
      { afterQuest: 'moor_captain_vane', text: 'Years I\'ve watched this road. It\'s yours to walk now, all the way to the cliffs.' },
    ],
    availableQuests: ['moor_cutpurse_road', 'moor_footpad_patrol', 'moor_thug_camp', 'moor_grix_the_cunning', 'moor_raiders', 'moor_smugglers', 'moor_lieutenants', 'moor_captain_vane'],
  },
  'moor_quartermaster': {
    id: 'moor_quartermaster',
    name: 'Quartermaster Hale',
    role: 'shop' as NPCDataConfig['role'],
    x: 330,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'The road north eats boots and courage. I sell replacements for one of those.',
      quests: 'Brannock hands out the work. I hand out what keeps you alive doing it.',
      complete: 'Signed, sealed, sold. Off you go.',
      goodbye: 'Keep your purse close on the moor. The brigands aren\'t picky.',
    },
    availableQuests: [],
    shopItems: ['greater_health_potion', 'greater_energy_potion', 'agility_potion', 'strength_potion', 'stamina_potion', 'swiftness_potion', 'mending_stone', 'attack_scroll', 'defense_scroll', 'scroll_fog_bank', 'scroll_rain_cloud', 'mithril_ore', 'glyphcloth_bolt', 'enchanted_dust'],
  },
  'cliffs_beaconkeeper': {
    id: 'cliffs_beaconkeeper',
    name: 'Beaconkeeper Rook',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Hold on to something. The wind up here has opinions.',
      quests: 'Drakes on the scree, harpies singing up gales on the crag, wyverns in the roosts over the waves and the Matriarch on top of the sea stack. Start with the drakes: I need that path to the beacon.',
      complete: 'That\'s one less thing trying to blow out my beacon. Take this.',
      goodbye: 'The moor\'s back through the portal behind me. It\'s flatter.',
    },
    lines: [
      { duringQuest: 'cliffs_harpy_matriarch', text: 'The sea stack is a long climb. Don\'t look down, and don\'t look at the Matriarch either.' },
      { afterQuest: 'cliffs_harpy_matriarch', text: 'Quiet skies. Next the road climbs to the peaks, and the trolls up there are worse than any harpy.' },
    ],
    availableQuests: ['cliffs_drake_scree', 'cliffs_windcallers', 'cliffs_wyvern_roosts', 'cliffs_wyvern_cull', 'cliffs_harpy_matriarch'],
  },
  'cliffs_trader': {
    id: 'cliffs_trader',
    name: 'Ropemaker Isla',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Rope, potions, bandages. Mostly rope. You\'ll want rope up here.',
      quests: 'Rook runs the beacon and the work around it. I run the rope.',
      complete: 'Done. Tie off before you look down.',
      goodbye: 'Keep low in the wind, and away from the edge.',
    },
    availableQuests: [],
    shopItems: ['greater_health_potion', 'orange_potion', 'greater_mana_potion', 'antidote', 'bandage', 'elixir_of_speed', 'return_scroll', 'food_rye_bread', 'elixir_of_fortitude'],
  },
  'peaks_summitwarden': {
    id: 'peaks_summitwarden',
    name: 'Summitwarden Isk',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Keep moving. Standing still is how the cold gets in.',
      quests: 'Rimecrest trolls camp on the lower snowfield, wendigos hunt the ledges, wraiths drift on the ice shelves and the hexers chant on the caps. Start with the camp; it\'s in everyone\'s way.',
      complete: 'Good work. Here, take this before your fingers stop working.',
      goodbye: 'The cliffs are back through the portal behind me. Warmer, if you call that warm.',
    },
    lines: [
      { duringQuest: 'peaks_avalanche_golem', text: 'Climb the shelves to the summit. Don\'t shout. Seriously, don\'t shout.' },
      { afterQuest: 'peaks_avalanche_golem', text: 'The road runs all the way up now. Rook will see our flag from the beacon.' },
    ],
    availableQuests: ['peaks_rimecrest_camp', 'peaks_wendigos', 'peaks_ice_wraiths', 'peaks_wraith_vigil', 'peaks_rimecrest_hexers', 'peaks_avalanche_golem'],
  },
  'peaks_trader': {
    id: 'peaks_trader',
    name: 'Hearthkeeper Signe',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Warm stew, strong potions, thick bandages. Up here the cold is the first thing that tries to kill you.',
      quests: 'Isk has the work on the peaks. I just keep the fire going for when you come back.',
      complete: 'Good. Eat the stew while it\'s hot.',
      goodbye: 'Watch the snow above you. It moves.',
    },
    availableQuests: [],
    shopItems: ['potion_major_healing', 'greater_mana_potion', 'elixir_of_fortitude', 'flask_of_the_titans', 'attack_scroll', 'defense_scroll', 'silk_bandage', 'spiced_bear_stew', 'antidote', 'return_scroll', 'power_elixir'],
  },
  'dusk_lampwarden': {
    id: 'dusk_lampwarden',
    name: 'Lampwarden Orrin',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Stay where the lamps reach. Out past them it\'s wolves, and after dark it\'s worse than wolves.',
      quests: 'Wolves on the vale floor, sick ones on the outcrops, dire wolves in the hollow, and up on the star blooms the Moonhowlers. Start with the road.',
      complete: 'Good. That\'s another stretch of road I don\'t have to light twice. Take this.',
      goodbye: 'The moor\'s back through the portal behind me, if you\'d rather have wind than wolves.',
    },
    lines: [
      { duringQuest: 'dusk_moonhowlers', text: 'The Moonhowlers were people once. Some of them still are, in daylight. That\'s the hard part.' },
      { afterQuest: 'dusk_grimfang', text: 'With Grimfang gone, I might even sleep. Don\'t tell the lamps.' },
    ],
    availableQuests: ['dusk_wolf_road', 'dusk_wolf_pelts', 'dusk_wolf_cull', 'dusk_thin_the_pack', 'dusk_rabid_alphas', 'dusk_dire_alpha', 'dusk_moonhowlers', 'dusk_grimfang'],
  },
  'dusk_trader': {
    id: 'dusk_trader',
    name: 'Candlewright Ena',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Candles, potions, bandages. Buy before dark. After dark, everything in the vale costs more.',
      quests: 'Orrin keeps the lamps and hands out the work. I keep you supplied for it.',
      complete: 'Good. Keep a light with you.',
      goodbye: 'Walk where the lamps are lit.',
    },
    availableQuests: [],
    shopItems: ['greater_health_potion', 'orange_potion', 'greater_mana_potion', 'antidote', 'bandage', 'elixir_of_speed', 'return_scroll', 'food_rye_bread'],
  },
  'blood_gravewatch': {
    id: 'blood_gravewatch',
    name: 'Gravewatch Mira',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_guard',
    dialogue: {
      greeting: 'Mind the red light. It isn\'t the moon\'s fault, but everything out here acts like it is.',
      quests: 'The dead walk the floor, the rats carry what killed them, and the Ember Vigil cut down anything that moves, the living included. Up on the high ledge, the one who raised them all.',
      complete: 'One more I won\'t have to bury twice. Here.',
      goodbye: 'The vale\'s back through the portal behind me. It\'s darker there, but quieter.',
    },
    lines: [
      { duringQuest: 'blood_zealots', text: 'The Vigil came to burn out the dead. Then they decided the rest of us looked suspicious too.' },
      { afterQuest: 'blood_malakar', text: 'Malakar\'s gone. The dead out there are just dead now. I can work with that.' },
    ],
    availableQuests: ['blood_risen_dead', 'blood_plague_rats', 'blood_zealots', 'blood_bone_wall', 'blood_cauldrons', 'blood_malakar'],
  },
  'blood_trader': {
    id: 'blood_trader',
    name: 'Sutler Morn',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Potions, bandages, a hot meal. I don\'t ask what you\'re fighting out there, and you don\'t ask about the prices.',
      quests: 'Mira keeps the graves and the work. I keep the gravewatch fed.',
      complete: 'Done. Come back in one piece.',
      goodbye: 'When the moon goes red, stay near the fires.',
    },
    availableQuests: [],
    shopItems: ['potion_major_healing', 'greater_health_potion', 'greater_mana_potion', 'elixir_of_fortitude', 'antidote', 'silk_bandage', 'spiced_bear_stew', 'return_scroll'],
  },
  'amber_grovekeeper': {
    id: 'amber_grovekeeper',
    name: 'Grovekeeper Fenn',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Hear that? Saws. Every morning, a little closer.',
      quests: 'The Gearwright Company is cutting the grove for profit, and the old trees have started cutting back, at anyone. East of the clearing, the farmstead\'s scarecrows walk on their own now, and the woman who calls herself the Duchess says the farm is hers.',
      complete: 'The grove will remember that. So will I.',
      goodbye: 'The savanna\'s back through the portal behind me. Mind the Tuskmire on the way.',
    },
    lines: [
      { duringQuest: 'amber_gearwright_loggers', text: 'They mark the oldest trees first. Red paint, a ledger number. That\'s all a thousand years is to them.' },
      { afterQuest: 'amber_elder_treant', text: 'The Elder is at rest. The grove\'s quieter. Not happier, but quieter.' },
    ],
    availableQuests: ['amber_gearwright_loggers', 'amber_blighted_treants', 'amber_scarecrows', 'amber_duchess', 'amber_elder_treant'],
  },
  'amber_trader': {
    id: 'amber_trader',
    name: 'Peddler Hobb',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Potions, bandages, bread. The apples are free, as long as you don\'t ask which scarecrow I took them from.',
      quests: 'Ask Fenn about the grove; I only sell what gets you through it.',
      complete: 'Pleasure doing business. Mind the branches on your way.',
      goodbye: 'If a scarecrow waves at you, don\'t wave back.',
    },
    availableQuests: [],
    shopItems: ['greater_health_potion', 'orange_potion', 'greater_mana_potion', 'antidote', 'bandage', 'elixir_of_speed', 'return_scroll', 'food_rye_bread'],
  },
  'canopy_ranger': {
    id: 'canopy_ranger',
    name: 'Canopy Ranger Ilse',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Keep your voice down. Up here, everything is listening.',
      quests: 'The Thornpaw keep the deep canopy and they\'ve decided we don\'t belong in it. The Weavers string the high ledges with silk I could use, the bears have come up out of the caves hungry, and something with a lot of legs nests in the fern hollow.',
      complete: 'Good. The canopy owes you one. So do I.',
      goodbye: 'Amber Grove is back through the portal behind me, if you need sunlight.',
    },
    lines: [
      { duringQuest: 'canopy_thornpaw', text: 'They aren\'t cruel. They\'re keepers. They just don\'t see much difference between a woodcutter and a ranger.' },
      { afterQuest: 'canopy_venomancer', text: 'The hollow is quiet. I can hear the canopy again, and it sounds like rain.' },
    ],
    availableQuests: ['canopy_thornpaw', 'canopy_spider_silk', 'canopy_bears', 'canopy_ursula', 'canopy_venomancer'],
  },
  'canopy_trader': {
    id: 'canopy_trader',
    name: 'Branch Trader Lio',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Potions, stew and silk bandages, all hauled up a very tall tree. The prices reflect the climb.',
      quests: 'Ilse knows the canopy and has the work. I just sell up here because nobody else will.',
      complete: 'Pleasure. Don\'t drop anything; it\'s a long way down.',
      goodbye: 'The webs in the high branches aren\'t decoration.',
    },
    availableQuests: [],
    shopItems: ['potion_major_healing', 'greater_health_potion', 'greater_mana_potion', 'elixir_of_fortitude', 'antidote', 'silk_bandage', 'spiced_bear_stew', 'return_scroll'],
  },
  'deep_delver': {
    id: 'deep_delver',
    name: 'Delver Bram',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_explorer',
    dialogue: {
      greeting: 'Watch your head. And your feet. And, honestly, everything else.',
      quests: 'Kobolds dig these caves faster than I can map them, and their shaman keeps them at it. Past them the Clodkin chew through the crystal seams, the glowing ooze eats whatever falls, and at the bottom sits something that calls itself a king.',
      complete: 'That\'s another stretch of the map I can draw without guessing.',
      goodbye: 'The cliffs are back through the portal behind me. Mind the drakes on the way up.',
    },
    lines: [
      { duringQuest: 'deep_clodkin', text: 'The Clodkin aren\'t digging for anything. They just eat the stone. The crystal ones are the ones they like best.' },
      { afterQuest: 'deep_crystal_king', text: 'No king down there now. Just crystal, and a long way down. I think I\'ll finish the map.' },
    ],
    availableQuests: ['deep_kobold_raiders', 'deep_tunnelers', 'deep_kobold_miners', 'deep_kobold_shaman', 'deep_clodkin', 'deep_deepglow_ooze', 'deep_crystal_king'],
  },
  'deep_trader': {
    id: 'deep_trader',
    name: 'Supply-keeper Brask',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Last stock before the dark. Potions, bandages, rations. Everything down here is a long walk back.',
      quests: 'Bram knows the caves and has the jobs. I just make sure you come back up.',
      complete: 'Good. Count your potions before you go deeper.',
      goodbye: 'Listen for the drip. Where it stops, something drank it.',
    },
    availableQuests: [],
    shopItems: ['greater_health_potion', 'orange_potion', 'greater_mana_potion', 'antidote', 'bandage', 'elixir_of_speed', 'return_scroll', 'food_rye_bread', 'elixir_of_fortitude'],
  },
  'wastes_ashwarden': {
    id: 'wastes_ashwarden',
    name: 'Ashwarden Kael',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_warrior',
    dialogue: {
      greeting: 'Breathe through your teeth. The ash gets everywhere.',
      quests: 'Fire spills out of the ground here: imps, slugs, drakes hatching in the cinders. A magma golem walks the high step, and something bigger waits at the east end that the imps call their master.',
      complete: 'One less fire to watch. There\'s always another.',
      goodbye: 'The shore is back through the portal behind me. Cool water, if you need it.',
    },
    lines: [
      { duringQuest: 'wastes_drake_brood', text: 'Young ones, still soft. Kill them now or fight them grown.' },
      { afterQuest: 'wastes_infernal', text: 'The Infernal is down. The ground\'s still hot, but it\'s only ground now.' },
    ],
    availableQuests: ['wastes_imps_and_slugs', 'wastes_flame_imps', 'wastes_drake_brood', 'wastes_magma_golem', 'wastes_infernal'],
  },
  'wastes_trader': {
    id: 'wastes_trader',
    name: 'Ash-peddler Corvin',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'Potions for burns, and potions so you don\'t get them. Out here you\'ll want both.',
      quests: 'Kael has the work. I\'ve got what keeps you from catching fire doing it.',
      complete: 'Good trade. Drink the cold one first.',
      goodbye: 'If the ground glows, walk around it.',
    },
    availableQuests: [],
    shopItems: ['potion_major_healing', 'greater_health_potion', 'greater_mana_potion', 'elixir_of_fortitude', 'antidote', 'silk_bandage', 'spiced_bear_stew', 'return_scroll', 'fire_protection_potion'],
  },
  'dunes_sandwarden': {
    id: 'dunes_sandwarden',
    name: 'Sandwarden Tova',
    role: 'quest_giver' as NPCDataConfig['role'],
    x: 600,
    y: 1250,
    sprite: 'npc_ranger',
    dialogue: {
      greeting: 'Drink before you\'re thirsty. Out here, thirsty is too late.',
      quests: 'There\'s a temple under the sand, and it\'s waking up. Scarabs boil out of the dunes, centaur scouts ride the mesas, naga sing from the high steps, and the one who dug the temple open never came back up the same.',
      complete: 'The dunes will cover it all again by morning. Good.',
      goodbye: 'The wastes are back through the portal behind me. Hotter, somehow.',
    },
    lines: [
      { duringQuest: 'dunes_naga', text: 'Don\'t listen to the singing. Whatever it promises, it isn\'t true.' },
      { afterQuest: 'dunes_guardian_trial', text: 'The archaeologist is at rest. I\'ll see that the temple stays buried this time.' },
    ],
    availableQuests: ['dunes_scarabs', 'dunes_centaurs', 'dunes_naga', 'dunes_guardian_trial'],
  },
  'dunes_trader': {
    id: 'dunes_trader',
    name: 'Caravan Trader Asad',
    role: 'shop' as NPCDataConfig['role'],
    x: 340,
    y: 1250,
    sprite: 'npc_merchant',
    dialogue: {
      greeting: 'The caravan stops here, so I stop here. Potions, bandages, stew, and something for the heat.',
      quests: 'Tova has the work in the dunes. I have water, or close to it.',
      complete: 'A fair trade. Travel in the shade.',
      goodbye: 'The sand hides things. Step where others stepped.',
    },
    availableQuests: [],
    shopItems: ['potion_major_healing', 'greater_mana_potion', 'elixir_of_fortitude', 'flask_of_the_titans', 'attack_scroll', 'defense_scroll', 'silk_bandage', 'spiced_bear_stew', 'antidote', 'return_scroll', 'power_elixir', 'fire_protection_potion'],
  },
};

export const MOB_CONFIG: Record<string, MobDataConfig> = {
  'training_dummy': {
    id: 'training_dummy',
    name: 'Training Dummy',
    type: 'training_dummy',
    mobType: 'training',
    aiType: 'stationary',
    aggroRange: 0,
    respawnTime: 0,
    harmless: true,
    death: { soulWisp: false },
    hp: 9999,
    level: 1,
    xp: 0,
    color: '#8b4513',
    width: 60,
    height: 50,
    movementStats: {
      moveSpeed: 0,
      jumpPower: 0,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 0,
      deceleration: 0,
    },
  },
  'snail': {
    id: 'snail',
    name: 'Blue Snail',
    type: 'snail',
    mobType: 'critter',
    aiType: 'skittish',
    aggroRange: 0,
    respawnTime: 10,
    hp: 15,
    level: 1,
    xp: 5,
    color: '#87ceeb',
    width: 40,
    height: 40,
  },
  'mushroom': {
    id: 'mushroom',
    name: 'Orange Mushroom',
    type: 'mushroom',
    mobType: 'normal',
    aiType: 'brawler',
    aggroRange: 120,
    respawnTime: 20,
    abilities: [{ id: 'mushroom_spores', weight: 1 }],
    attackRange: 60,
    hp: 50,
    level: 3,
    xp: 25,
    color: '#ffa500',
    width: 50,
    height: 50,
  },
  'slime': {
    id: 'slime',
    name: 'Green Slime',
    type: 'slime',
    mobType: 'normal',
    aiType: 'brawler',
    aggroRange: 100,
    respawnTime: 15,
    attackRange: 50,
    split: { into: 'slimeling', count: 2 },
    hp: 20,
    level: 2,
    xp: 15,
    color: '#32cd32',
    width: 40,
    height: 40,
  },
  'slimeling': {
    id: 'slimeling',
    name: 'Slimeling',
    type: 'slimeling',
    mobType: 'normal',
    aiType: 'brawler',
    aggroRange: 120,
    respawnTime: 0,
    attackRange: 40,
    reactionMs: 600,
    hp: 8,
    level: 1,
    xp: 4,
    color: '#7cfc00',
    width: 26,
    height: 26,
    movementStats: {
      moveSpeed: 65,
      jumpPower: 420,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'pig': {
    id: 'pig',
    name: 'Wild Pig',
    type: 'pig',
    mobType: 'normal',
    aiType: 'skittish',
    aggroRange: 0,
    respawnTime: 15,
    attackRange: 60,
    hp: 30,
    level: 1,
    xp: 10,
    color: '#ffb6c1',
    width: 60,
    height: 50,
  },
  'test_pig': {
    id: 'test_pig',
    name: 'Test Pig',
    type: 'pig',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 15,
    harmless: true,
    hp: 30,
    level: 1,
    xp: 10,
    color: '#ffd700',
    width: 60,
    height: 50,
  },
  'stump': {
    id: 'stump',
    name: 'Stump',
    type: 'stump',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 25,
    attackRange: 70,
    hp: 80,
    level: 4,
    xp: 35,
    color: '#8b6914',
    width: 50,
    height: 60,
    movementStats: {
      moveSpeed: 30,
      jumpPower: 300,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 100,
      deceleration: 100,
    },
  },
  'evil_eye': {
    id: 'evil_eye',
    name: 'Evil Eye',
    type: 'evil_eye',
    mobType: 'normal',
    aiType: 'caster',
    aggroRange: 150,
    respawnTime: 20,
    attackRange: 180,
    hp: 60,
    level: 3,
    xp: 30,
    color: '#9400d3',
    width: 40,
    height: 40,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 0.8,
      terminalVelocity: 800,
      acceleration: 300,
      deceleration: 300,
    },
  },
  'fire_boar': {
    id: 'fire_boar',
    name: 'Fire Boar',
    type: 'fire_boar',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 130,
    respawnTime: 30,
    baseDamage: 6,
    attackRange: 90,
    death: { style: 'fire' },
    hp: 120,
    level: 5,
    xp: 50,
    color: '#ff4500',
    width: 70,
    height: 55,
    movementStats: {
      moveSpeed: 70,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 250,
      deceleration: 200,
    },
  },
  'stone_golem': {
    id: 'stone_golem',
    name: 'Stone Golem',
    type: 'stone_golem',
    mobType: 'elite',
    aiType: 'juggernaut',
    aggroRange: 180,
    respawnTime: 60,
    baseDamage: 10,
    attackRange: 100,
    hp: 200,
    level: 7,
    xp: 80,
    color: '#808080',
    width: 70,
    height: 80,
    movementStats: {
      moveSpeed: 25,
      jumpPower: 200,
      gravityScale: 1.2,
      terminalVelocity: 1200,
      acceleration: 80,
      deceleration: 80,
    },
  },
  'moonfall_stone_golem': {
    id: 'moonfall_stone_golem',
    name: 'Stone Golem Guardian',
    type: 'moonfall_stone_golem',
    mobType: 'elite',
    aiType: 'juggernaut',
    aggroRange: 240,
    respawnTime: 60,
    abilities: [{ id: 'golem_ground_slam', weight: 2 }, { id: 'golem_boulder_toss', weight: 1 }],
    baseDamage: 6,
    attackRange: 110,
    phases: [
      { hpThreshold: 0.5, name: 'Awakened Core', abilities: ['golem_slam_awakened', 'golem_boulder_toss'], damageMultiplier: 1.15, speedMultiplier: 1.3, armorMs: 4000, armorDamageMultiplier: 0.5 },
    ],
    death: { style: 'physical', scale: 1.4, durationMs: 1800 },
    hp: 520,
    level: 5,
    xp: 90,
    color: '#5f6b78',
    width: 84,
    height: 96,
    movementStats: {
      moveSpeed: 40,
      jumpPower: 220,
      gravityScale: 1.2,
      terminalVelocity: 1200,
      acceleration: 90,
      deceleration: 90,
    },
  },
  'ice_drake': {
    id: 'ice_drake',
    name: 'Ice Drake',
    type: 'ice_drake',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 160,
    respawnTime: 35,
    baseDamage: 8,
    attackRange: 200,
    hp: 150,
    level: 6,
    xp: 65,
    color: '#add8e6',
    width: 65,
    height: 50,
    movementStats: {
      moveSpeed: 90,
      jumpPower: 600,
      gravityScale: 0.7,
      terminalVelocity: 700,
      acceleration: 350,
      deceleration: 350,
    },
  },
  'dark_stump': {
    id: 'dark_stump',
    name: 'Dark Stump',
    type: 'dark_stump',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 45,
    attackRange: 80,
    hp: 250,
    level: 8,
    xp: 100,
    color: '#2f1b14',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 40,
      jumpPower: 350,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 150,
      deceleration: 150,
    },
  },
  'elite_slime': {
    id: 'elite_slime',
    name: 'Elite Slime',
    type: 'elite_slime',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 200,
    respawnTime: 120,
    attackRange: 60,
    hp: 500,
    level: 8,
    xp: 200,
    color: '#00ff00',
    width: 70,
    height: 70,
    movementStats: {
      moveSpeed: 60,
      jumpPower: 500,
      gravityScale: 0.9,
      terminalVelocity: 900,
      acceleration: 250,
      deceleration: 250,
    },
  },
  'shadow_bat': {
    id: 'shadow_bat',
    name: 'Shadow Bat',
    type: 'shadow_bat',
    mobType: 'normal',
    aiType: 'skirmisher',
    aggroRange: 180,
    respawnTime: 20,
    attackRange: 120,
    hp: 40,
    level: 4,
    xp: 30,
    color: '#4b0082',
    width: 45,
    height: 35,
    movementStats: {
      moveSpeed: 120,
      jumpPower: 700,
      gravityScale: 0.5,
      terminalVelocity: 600,
      acceleration: 400,
      deceleration: 400,
    },
  },
  'cursed_armor': {
    id: 'cursed_armor',
    name: 'Cursed Armor',
    type: 'cursed_armor',
    mobType: 'elite',
    aiType: 'patrol',
    aggroRange: 160,
    respawnTime: 90,
    attackRange: 80,
    hp: 300,
    level: 9,
    xp: 120,
    color: '#696969',
    width: 55,
    height: 75,
    movementStats: {
      moveSpeed: 45,
      jumpPower: 350,
      gravityScale: 1.1,
      terminalVelocity: 1100,
      acceleration: 150,
      deceleration: 150,
    },
  },
  'hollow_keeper': {
    id: 'hollow_keeper',
    name: 'The Hollow Keeper',
    type: 'hollow_keeper',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 300,
    respawnTime: 120,
    abilities: [{ id: 'keeper_rake', weight: 4 }, { id: 'keeper_slam', weight: 2 }],
    baseDamage: 6,
    attackRange: 110,
    leashRange: 2200,
    phases: [
      { hpThreshold: 0.6, name: 'Fogbound', abilities: ['keeper_rake', 'keeper_slam', 'hollow_wail'], damageMultiplier: 1.3, speedMultiplier: 1.2, color: '#4a5a55' },
      { hpThreshold: 0.35, name: 'Hollow Wrath', abilities: ['keeper_rake', 'keeper_slam', 'hollow_wail', 'hollow_grasp'], damageMultiplier: 1.7, speedMultiplier: 1.4, color: '#2f3b38' },
    ],
    death: { style: 'shadow', scale: 1.5, durationMs: 1400 },
    hp: 3400,
    level: 8,
    xp: 700,
    color: '#696969',
    width: 80,
    height: 110,
    movementStats: {
      moveSpeed: 55,
      jumpPower: 0,
      gravityScale: 1.1,
      terminalVelocity: 1100,
      acceleration: 170,
      deceleration: 170,
    },
  },
  'crystal_spider': {
    id: 'crystal_spider',
    name: 'Crystal Spider',
    type: 'crystal_spider',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 25,
    attackRange: 80,
    hp: 180,
    level: 10,
    xp: 90,
    color: '#b0e0e6',
    width: 55,
    height: 45,
    movementStats: {
      moveSpeed: 100,
      jumpPower: 500,
      gravityScale: 0.9,
      terminalVelocity: 900,
      acceleration: 350,
      deceleration: 350,
    },
  },
  'gem_golem': {
    id: 'gem_golem',
    name: 'Gem Golem',
    type: 'gem_golem',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 160,
    respawnTime: 90,
    attackRange: 100,
    hp: 400,
    level: 12,
    xp: 180,
    color: '#e6b800',
    width: 80,
    height: 90,
    movementStats: {
      moveSpeed: 20,
      jumpPower: 150,
      gravityScale: 1.3,
      terminalVelocity: 1300,
      acceleration: 60,
      deceleration: 60,
    },
  },
  'void_wraith': {
    id: 'void_wraith',
    name: 'Void Wraith',
    type: 'void_wraith',
    mobType: 'rare',
    aiType: 'caster',
    aggroRange: 250,
    respawnTime: 180,
    attackRange: 180,
    hp: 800,
    level: 15,
    xp: 400,
    color: '#330033',
    width: 60,
    height: 70,
    movementStats: {
      moveSpeed: 110,
      jumpPower: 600,
      gravityScale: 0.4,
      terminalVelocity: 500,
      acceleration: 400,
      deceleration: 400,
    },
  },
  'cave_bat': {
    id: 'cave_bat',
    name: 'Cave Bat',
    type: 'cave_bat',
    mobType: 'normal',
    aiType: 'skirmisher',
    aggroRange: 160,
    respawnTime: 15,
    attackRange: 120,
    hp: 100,
    level: 8,
    xp: 55,
    color: '#3d3d5c',
    width: 40,
    height: 35,
    movementStats: {
      moveSpeed: 130,
      jumpPower: 700,
      gravityScale: 0.4,
      terminalVelocity: 550,
      acceleration: 450,
      deceleration: 450,
    },
  },
  'frost_elemental': {
    id: 'frost_elemental',
    name: 'Frost Elemental',
    type: 'frost_elemental',
    mobType: 'elite',
    aiType: 'caster',
    aggroRange: 180,
    respawnTime: 60,
    attackRange: 150,
    hp: 350,
    level: 11,
    xp: 150,
    color: '#70d4ff',
    width: 65,
    height: 75,
    movementStats: {
      moveSpeed: 50,
      jumpPower: 400,
      gravityScale: 0.8,
      terminalVelocity: 800,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'lava_slug': {
    id: 'lava_slug',
    name: 'Lava Slug',
    type: 'lava_slug',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 20,
    attackRange: 60,
    hp: 220,
    level: 13,
    xp: 110,
    color: '#ff6600',
    width: 50,
    height: 40,
    movementStats: {
      moveSpeed: 35,
      jumpPower: 200,
      gravityScale: 1.2,
      terminalVelocity: 1200,
      acceleration: 100,
      deceleration: 100,
    },
  },
  'infernal': {
    id: 'infernal',
    name: 'Infernal',
    type: 'infernal',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 300,
    respawnTime: 300,
    attackRange: 110,
    phases: [
      { hpThreshold: 0.7, name: 'Burning Rage', damageMultiplier: 1.2, color: '#ff3300' },
      { hpThreshold: 0.4, name: 'Hellfire', damageMultiplier: 1.5, speedMultiplier: 1.3, color: '#ff6600' },
      { hpThreshold: 0.15, name: 'Final Stand', damageMultiplier: 2, speedMultiplier: 1.5, color: '#ffcc00' },
    ],
    hp: 2000,
    level: 20,
    xp: 1000,
    color: '#cc0000',
    width: 100,
    height: 120,
    movementStats: {
      moveSpeed: 40,
      jumpPower: 300,
      gravityScale: 1.5,
      terminalVelocity: 1500,
      acceleration: 100,
      deceleration: 100,
    },
  },
  'snow_wolf': {
    id: 'snow_wolf',
    name: 'Snow Wolf',
    type: 'snow_wolf',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 20,
    attackRange: 80,
    hp: 160,
    level: 9,
    xp: 70,
    color: '#e8e8f0',
    width: 55,
    height: 45,
    movementStats: {
      moveSpeed: 110,
      jumpPower: 550,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 400,
      deceleration: 400,
    },
  },
  'magma_golem': {
    id: 'magma_golem',
    name: 'Magma Golem',
    type: 'magma_golem',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 160,
    respawnTime: 90,
    attackRange: 120,
    hp: 500,
    level: 16,
    xp: 250,
    color: '#b33000',
    width: 80,
    height: 90,
    movementStats: {
      moveSpeed: 20,
      jumpPower: 150,
      gravityScale: 1.4,
      terminalVelocity: 1400,
      acceleration: 60,
      deceleration: 60,
    },
  },
  'giant_bee': {
    id: 'giant_bee',
    name: 'Giant Bee',
    type: 'giant_bee',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 15,
    attackRange: 80,
    hp: 90,
    level: 5,
    xp: 40,
    color: '#ffcc00',
    width: 45,
    height: 40,
    movementStats: {
      moveSpeed: 100,
      jumpPower: 600,
      gravityScale: 0.5,
      terminalVelocity: 600,
      acceleration: 400,
      deceleration: 400,
    },
  },
  'forest_troll': {
    id: 'forest_troll',
    name: 'Forest Troll',
    type: 'forest_troll',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 30,
    attackRange: 110,
    hp: 130,
    level: 7,
    xp: 60,
    color: '#556b2f',
    width: 60,
    height: 70,
    movementStats: {
      moveSpeed: 45,
      jumpPower: 350,
      gravityScale: 1.1,
      terminalVelocity: 1100,
      acceleration: 150,
      deceleration: 150,
    },
  },
  'flame_imp': {
    id: 'flame_imp',
    name: 'Flame Imp',
    type: 'flame_imp',
    mobType: 'normal',
    aiType: 'caster',
    aggroRange: 200,
    respawnTime: 25,
    attackRange: 150,
    hp: 250,
    level: 14,
    xp: 130,
    color: '#ff3300',
    width: 40,
    height: 50,
    movementStats: {
      moveSpeed: 90,
      jumpPower: 500,
      gravityScale: 0.7,
      terminalVelocity: 700,
      acceleration: 350,
      deceleration: 350,
    },
  },
  'obsidian_sentinel': {
    id: 'obsidian_sentinel',
    name: 'Obsidian Sentinel',
    type: 'obsidian_sentinel',
    mobType: 'elite',
    aiType: 'patrol',
    aggroRange: 200,
    respawnTime: 120,
    attackRange: 110,
    hp: 600,
    level: 17,
    xp: 300,
    color: '#1a1a2e',
    width: 70,
    height: 85,
    movementStats: {
      moveSpeed: 30,
      jumpPower: 200,
      gravityScale: 1.3,
      terminalVelocity: 1300,
      acceleration: 80,
      deceleration: 80,
    },
  },
  'phoenix': {
    id: 'phoenix',
    name: 'Phoenix',
    type: 'phoenix',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 250,
    respawnTime: 300,
    attackRange: 200,
    phases: [
      { hpThreshold: 0.5, name: 'Rising Flames', damageMultiplier: 1.3, color: '#ff4500' },
      { hpThreshold: 0.1, name: 'Rebirth', damageMultiplier: 2, speedMultiplier: 2, color: '#ffff00' },
    ],
    hp: 1200,
    level: 19,
    xp: 600,
    color: '#ff8c00',
    width: 70,
    height: 60,
    movementStats: {
      moveSpeed: 120,
      jumpPower: 800,
      gravityScale: 0.3,
      terminalVelocity: 400,
      acceleration: 500,
      deceleration: 500,
    },
  },
  'jellyfish': {
    id: 'jellyfish',
    name: 'Jellyfish',
    type: 'jellyfish',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 12,
    hp: 25,
    level: 2,
    xp: 8,
    color: '#da70d6',
    width: 35,
    height: 40,
    movementStats: {
      moveSpeed: 30,
      jumpPower: 500,
      gravityScale: 0.4,
      terminalVelocity: 500,
      acceleration: 100,
      deceleration: 100,
    },
  },
  'wild_boar': {
    id: 'wild_boar',
    name: 'Wild Boar',
    type: 'wild_boar',
    mobType: 'normal',
    aiType: 'charger',
    aggroRange: 110,
    respawnTime: 18,
    attackRange: 80,
    hp: 100,
    level: 6,
    xp: 45,
    color: '#8b4513',
    width: 65,
    height: 50,
    movementStats: {
      moveSpeed: 75,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 300,
      deceleration: 250,
    },
  },
  'crystal_elemental': {
    id: 'crystal_elemental',
    name: 'Crystal Elemental',
    type: 'crystal_elemental',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 150,
    respawnTime: 30,
    attackRange: 100,
    hp: 200,
    level: 11,
    xp: 100,
    color: '#7fffd4',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 60,
      jumpPower: 400,
      gravityScale: 0.9,
      terminalVelocity: 900,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'shadow_stalker': {
    id: 'shadow_stalker',
    name: 'Shadow Stalker',
    type: 'shadow_stalker',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 200,
    respawnTime: 25,
    attackRange: 90,
    hp: 230,
    level: 13,
    xp: 120,
    color: '#2d0033',
    width: 50,
    height: 55,
    movementStats: {
      moveSpeed: 105,
      jumpPower: 550,
      gravityScale: 0.8,
      terminalVelocity: 800,
      acceleration: 380,
      deceleration: 380,
    },
  },
  'ancient_treant': {
    id: 'ancient_treant',
    name: 'Ancient Treant',
    type: 'ancient_treant',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 90,
    attackRange: 130,
    hp: 450,
    level: 15,
    xp: 220,
    color: '#2e8b57',
    width: 80,
    height: 100,
    movementStats: {
      moveSpeed: 20,
      jumpPower: 150,
      gravityScale: 1.3,
      terminalVelocity: 1300,
      acceleration: 60,
      deceleration: 60,
    },
  },
  'war_golem': {
    id: 'war_golem',
    name: 'War Golem',
    type: 'war_golem',
    mobType: 'elite',
    aiType: 'patrol',
    aggroRange: 180,
    respawnTime: 120,
    attackRange: 110,
    hp: 550,
    level: 16,
    xp: 280,
    color: '#8b8682',
    width: 75,
    height: 90,
    movementStats: {
      moveSpeed: 25,
      jumpPower: 180,
      gravityScale: 1.4,
      terminalVelocity: 1400,
      acceleration: 70,
      deceleration: 70,
    },
  },
  'void_walker': {
    id: 'void_walker',
    name: 'Void Walker',
    type: 'void_walker',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 220,
    respawnTime: 30,
    attackRange: 60,
    hp: 380,
    level: 18,
    xp: 200,
    color: '#1a0033',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 0.6,
      terminalVelocity: 600,
      acceleration: 300,
      deceleration: 300,
    },
  },
  'crystal_king': {
    id: 'crystal_king',
    name: 'Crystal King',
    type: 'crystal_king',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 250,
    respawnTime: 300,
    attackRange: 75,
    phases: [
      { hpThreshold: 0.6, name: 'Crystalline Fury', damageMultiplier: 1.3, color: '#00ffff' },
      { hpThreshold: 0.3, name: 'Shatter Storm', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#88ffff' },
      { hpThreshold: 0.1, name: 'Final Shatter', damageMultiplier: 2, speedMultiplier: 1.5, color: '#ffffff' },
    ],
    hp: 2600,
    level: 20,
    xp: 1300,
    color: '#2D3DB9',
    width: 82,
    height: 90,
    movementStats: {
      moveSpeed: 30,
      jumpPower: 250,
      gravityScale: 1.2,
      terminalVelocity: 1200,
      acceleration: 80,
      deceleration: 80,
    },
  },
  'leviathan': {
    id: 'leviathan',
    name: 'Leviathan of the Deep',
    type: 'leviathan',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 220,
    respawnTime: 600,
    attackRange: 90,
    phases: [
      { hpThreshold: 0.75, name: 'Tidal Fury', damageMultiplier: 1.2, color: '#0055aa' },
      { hpThreshold: 0.5, name: 'Summon the Deep', damageMultiplier: 1.4, speedMultiplier: 1.2, color: '#0077cc' },
      { hpThreshold: 0.25, name: 'Maelstrom', damageMultiplier: 1.8, speedMultiplier: 1.5, color: '#00aaff' },
      { hpThreshold: 0.1, name: 'Death Throes', damageMultiplier: 2.5, speedMultiplier: 2, color: '#00ddff' },
    ],
    hp: 3620,
    level: 22,
    xp: 1629,
    color: '#0D374E',
    width: 100,
    height: 142,
    movementStats: {
      moveSpeed: 35,
      jumpPower: 200,
      gravityScale: 1.3,
      terminalVelocity: 1200,
      acceleration: 90,
      deceleration: 90,
    },
  },
  'arch_lich_malakar': {
    id: 'arch_lich_malakar',
    name: 'Arch-Lich Malakar',
    type: 'arch_lich_malakar',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 400,
    respawnTime: 600,
    attackRange: 75,
    phases: [
      { hpThreshold: 0.75, name: 'Shadow Barrage', damageMultiplier: 1.2, color: '#6600aa' },
      { hpThreshold: 0.5, name: 'Necrotic Nova', damageMultiplier: 1.5, speedMultiplier: 1.2, color: '#9933cc' },
      { hpThreshold: 0.25, name: 'Soul Harvest', damageMultiplier: 2, speedMultiplier: 1.5, color: '#cc66ff' },
      { hpThreshold: 0.1, name: 'Apocalypse', damageMultiplier: 3, speedMultiplier: 2, color: '#ff00ff' },
    ],
    hp: 4000,
    level: 24,
    xp: 2000,
    color: '#330066',
    width: 80,
    height: 110,
    movementStats: {
      moveSpeed: 50,
      jumpPower: 400,
      gravityScale: 0.6,
      terminalVelocity: 600,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'pig_mom': {
    id: 'pig_mom',
    name: 'Pig Mom',
    type: 'pig_mom',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 250,
    respawnTime: 60,
    baseDamage: 15,
    attackRange: 120,
    phases: [
      { hpThreshold: 0.6, name: 'Maternal Fury', damageMultiplier: 1.3, speedMultiplier: 1.2, color: '#ff1493' },
      { hpThreshold: 0.25, name: 'Desperate Charge', damageMultiplier: 1.8, speedMultiplier: 1.5, color: '#ff0066' },
    ],
    hp: 1200,
    level: 10,
    xp: 200,
    color: '#ff69b4',
    width: 180,
    height: 130,
    movementStats: {
      moveSpeed: 35,
      jumpPower: 250,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 150,
      deceleration: 150,
    },
  },
  'young_fire_drake': {
    id: 'young_fire_drake',
    name: 'Young Fire Drake',
    type: 'young_fire_drake',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 150,
    respawnTime: 30,
    attackRange: 66,
    hp: 524,
    level: 18,
    xp: 149,
    color: '#E8734F',
    width: 70,
    height: 52,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 750,
      gravityScale: 0.5,
      terminalVelocity: 600,
      acceleration: 225,
      deceleration: 225,
    },
  },
  'wolf': {
    id: 'wolf',
    name: 'Wolf',
    type: 'wolf',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 30,
    attackRange: 70,
    hp: 220,
    level: 10,
    xp: 85,
    color: '#808080',
    width: 74,
    height: 60,
    movementStats: {
      moveSpeed: 73,
      jumpPower: 360,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 305,
      deceleration: 305,
    },
  },
  'grimfang_the_ravenous': {
    id: 'grimfang_the_ravenous',
    name: 'Grimfang the Ravenous',
    type: 'grimfang_the_ravenous',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 85,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ff8b69' },
      { hpThreshold: 0.2, name: 'Blood Moon', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#D02828' },
    ],
    hp: 2620,
    level: 18,
    xp: 1341,
    color: '#0E0F15',
    width: 101,
    height: 126,
    movementStats: {
      moveSpeed: 115,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 375,
      deceleration: 375,
    },
  },
  'darkwood_bear_elite': {
    id: 'darkwood_bear_elite',
    name: 'Ursula the Maneater',
    type: 'darkwood_bear_elite',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 150,
    respawnTime: 180,
    attackRange: 69,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ff8b69' },
    ],
    hp: 1048,
    level: 18,
    xp: 373,
    color: '#2E2A38',
    width: 78,
    height: 72,
    movementStats: {
      moveSpeed: 106,
      jumpPower: 470,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 360,
      deceleration: 360,
    },
  },
  'venomous_spider_elite': {
    id: 'venomous_spider_elite',
    name: 'Venomancer',
    type: 'venomous_spider_elite',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 95,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ff8b69' },
      { hpThreshold: 0.2, name: 'Venom Frenzy', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#E04060' },
    ],
    hp: 3355,
    level: 21,
    xp: 1557,
    color: '#8B0A1A',
    width: 129,
    height: 91,
    movementStats: {
      moveSpeed: 124,
      jumpPower: 530,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 390,
      deceleration: 390,
    },
  },
  'forest_spider': {
    id: 'forest_spider',
    name: 'Forest Spider',
    type: 'forest_spider',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 395,
    level: 15,
    xp: 125,
    color: '#5B3A29',
    width: 60,
    height: 47,
    movementStats: {
      moveSpeed: 76,
      jumpPower: 370,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 310,
      deceleration: 310,
    },
  },
  'rabid_wolf': {
    id: 'rabid_wolf',
    name: 'Rabid Wolf',
    type: 'rabid_wolf',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 65,
    hp: 251,
    level: 11,
    xp: 93,
    color: '#7A7C4A',
    width: 70,
    height: 54,
    movementStats: {
      moveSpeed: 79,
      jumpPower: 380,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 315,
      deceleration: 315,
    },
  },
  'brigand_cutpurse': {
    id: 'brigand_cutpurse',
    name: 'Brigand Cutpurse',
    type: 'brigand_cutpurse',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 116,
    level: 6,
    xp: 53,
    color: '#5E6B57',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 58,
      jumpPower: 390,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 170,
      deceleration: 170,
    },
  },
  'brigand_footpad': {
    id: 'brigand_footpad',
    name: 'Brigand Footpad',
    type: 'brigand_footpad',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 139,
    level: 7,
    xp: 61,
    color: '#8A7454',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 60,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 175,
      deceleration: 175,
    },
  },
  'darkwood_treant': {
    id: 'darkwood_treant',
    name: 'Darkwood Treant',
    type: 'darkwood_treant',
    mobType: 'normal',
    aiType: 'patrol',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 80,
    hp: 220,
    level: 10,
    xp: 85,
    color: '#4B5E3E',
    width: 40,
    height: 82,
    movementStats: {
      moveSpeed: 62,
      jumpPower: 410,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 180,
      deceleration: 180,
    },
  },
  'corrupted_dryad': {
    id: 'corrupted_dryad',
    name: 'Corrupted Dryad',
    type: 'corrupted_dryad',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 220,
    level: 10,
    xp: 85,
    color: '#7FA87A',
    width: 45,
    height: 65,
    movementStats: {
      moveSpeed: 64,
      jumpPower: 420,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 185,
      deceleration: 185,
    },
  },
  'giant_stag_beetle': {
    id: 'giant_stag_beetle',
    name: 'Giant Stag Beetle',
    type: 'giant_stag_beetle',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 30,
    attackRange: 62,
    hp: 724,
    level: 22,
    xp: 181,
    color: '#3B2A1E',
    width: 64,
    height: 38,
    movementStats: {
      moveSpeed: 79,
      jumpPower: 380,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 315,
      deceleration: 315,
    },
  },
  'moss_lurker': {
    id: 'moss_lurker',
    name: 'Moss Lurker',
    type: 'moss_lurker',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 63,
    hp: 395,
    level: 15,
    xp: 125,
    color: '#6B7A52',
    width: 66,
    height: 48,
    movementStats: {
      moveSpeed: 94,
      jumpPower: 430,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 340,
      deceleration: 340,
    },
  },
  'bandit_leader_grix': {
    id: 'bandit_leader_grix',
    name: 'Grix the Cunning',
    type: 'bandit_leader_grix',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 60,
    hp: 660,
    level: 10,
    xp: 425,
    color: '#4C5A6B',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 66,
      jumpPower: 430,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 190,
      deceleration: 190,
    },
  },
  'elder_treant': {
    id: 'elder_treant',
    name: 'Elder Treant',
    type: 'elder_treant',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 80,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ff60ab' },
      { hpThreshold: 0.2, name: 'Rootwrath', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#C8A030' },
    ],
    hp: 1975,
    level: 15,
    xp: 1125,
    color: '#3A4F32',
    width: 84,
    height: 128,
    movementStats: {
      moveSpeed: 70,
      jumpPower: 450,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'mountain_goat': {
    id: 'mountain_goat',
    name: 'Mountain Goat',
    type: 'mountain_goat',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 15,
    attackRange: 60,
    hp: 571,
    level: 19,
    xp: 157,
    color: '#B0A693',
    width: 58,
    height: 60,
    movementStats: {
      moveSpeed: 100,
      jumpPower: 450,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 350,
      deceleration: 350,
    },
  },
  'rock_elemental': {
    id: 'rock_elemental',
    name: 'Rock Elemental',
    type: 'rock_elemental',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#5E6A72',
    width: 60,
    height: 65,
    movementStats: {
      moveSpeed: 74,
      jumpPower: 470,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 210,
      deceleration: 210,
    },
  },
  'harpy_windcaller': {
    id: 'harpy_windcaller',
    name: 'Harpy Windcaller',
    type: 'harpy_windcaller',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 356,
    level: 14,
    xp: 117,
    color: '#6B7C93',
    width: 60,
    height: 52,
    movementStats: {
      moveSpeed: 76,
      jumpPower: 480,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 215,
      deceleration: 215,
    },
  },
  'mountain_troll': {
    id: 'mountain_troll',
    name: 'Mountain Troll',
    type: 'mountain_troll',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 779,
    level: 23,
    xp: 189,
    color: '#3E4F5C',
    width: 60,
    height: 84,
    movementStats: {
      moveSpeed: 78,
      jumpPower: 490,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 220,
      deceleration: 220,
    },
  },
  'ice_wraith': {
    id: 'ice_wraith',
    name: 'Ice Wraith',
    type: 'ice_wraith',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 671,
    level: 21,
    xp: 173,
    color: '#CBD6EE',
    width: 50,
    height: 62,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 225,
      deceleration: 225,
    },
  },
  'stone_drake': {
    id: 'stone_drake',
    name: 'Stone Drake',
    type: 'stone_drake',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 70,
    hp: 395,
    level: 15,
    xp: 125,
    color: '#B08B69',
    width: 80,
    height: 55,
    movementStats: {
      moveSpeed: 82,
      jumpPower: 760,
      gravityScale: 0.5,
      terminalVelocity: 600,
      acceleration: 230,
      deceleration: 230,
    },
  },
  'cave_bear': {
    id: 'cave_bear',
    name: 'Cave Bear',
    type: 'cave_bear',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 64,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#5C4632',
    width: 68,
    height: 60,
    movementStats: {
      moveSpeed: 109,
      jumpPower: 480,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 365,
      deceleration: 365,
    },
  },
  'harpy_matriarch': {
    id: 'harpy_matriarch',
    name: 'Harpy Matriarch',
    type: 'harpy_matriarch',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 90,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ffb886' },
      { hpThreshold: 0.2, name: 'Storm Shriek', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#C080FF' },
    ],
    hp: 2855,
    level: 19,
    xp: 1413,
    color: '#8B3A5C',
    width: 119,
    height: 101,
    movementStats: {
      moveSpeed: 86,
      jumpPower: 530,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 240,
      deceleration: 240,
    },
  },
  'avalanche_golem': {
    id: 'avalanche_golem',
    name: 'Avalanche Golem',
    type: 'avalanche_golem',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 85,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ff60ab' },
      { hpThreshold: 0.2, name: 'Landslide', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#A0E0FF' },
    ],
    hp: 4475,
    level: 25,
    xp: 1845,
    color: '#3A6B78',
    width: 104,
    height: 121,
    movementStats: {
      moveSpeed: 90,
      jumpPower: 550,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 250,
      deceleration: 250,
    },
  },
  'skeletal_warrior': {
    id: 'skeletal_warrior',
    name: 'Skeletal Warrior',
    type: 'skeletal_warrior',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 620,
    level: 20,
    xp: 165,
    color: '#C7C2AC',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 90,
      jumpPower: 550,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 250,
      deceleration: 250,
    },
  },
  'skeletal_mage': {
    id: 'skeletal_mage',
    name: 'Skeletal Mage',
    type: 'skeletal_mage',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 671,
    level: 21,
    xp: 173,
    color: '#9FA8B5',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 92,
      jumpPower: 560,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 255,
      deceleration: 255,
    },
  },
  'tomb_scarab': {
    id: 'tomb_scarab',
    name: 'Tomb Scarab',
    type: 'tomb_scarab',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 70,
    hp: 724,
    level: 22,
    xp: 181,
    color: '#4A3550',
    width: 80,
    height: 55,
    movementStats: {
      moveSpeed: 130,
      jumpPower: 550,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 400,
      deceleration: 400,
    },
  },
  'cursed_archaeologist': {
    id: 'cursed_archaeologist',
    name: 'Keeper Halloran',
    type: 'cursed_archaeologist',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 80,
    phases: [
      { hpThreshold: 0.5, name: 'Buried Wrath', damageMultiplier: 1.3, color: '#B0782E' },
      { hpThreshold: 0.2, name: 'Last Rite', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#D8A040' },
    ],
    hp: 5420,
    level: 28,
    xp: 2061,
    color: '#7A5A3C',
    width: 90,
    height: 98,
    movementStats: {
      moveSpeed: 94,
      jumpPower: 570,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 260,
      deceleration: 260,
    },
  },
  'kobold_miner': {
    id: 'kobold_miner',
    name: 'Kobold Miner',
    type: 'kobold_miner',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 356,
    level: 14,
    xp: 117,
    color: '#C9812F',
    width: 55,
    height: 53,
    movementStats: {
      moveSpeed: 70,
      jumpPower: 450,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'kobold_shaman': {
    id: 'kobold_shaman',
    name: 'Kobold Shaman',
    type: 'kobold_shaman',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#6E4A66',
    width: 55,
    height: 53,
    movementStats: {
      moveSpeed: 72,
      jumpPower: 460,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 205,
      deceleration: 205,
    },
  },
  'plague_rat': {
    id: 'plague_rat',
    name: 'Plague Rat',
    type: 'plague_rat',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#6E9438',
    width: 60,
    height: 36,
    movementStats: {
      moveSpeed: 85,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 325,
      deceleration: 325,
    },
  },
  'dire_wolf': {
    id: 'dire_wolf',
    name: 'Dire Wolf',
    type: 'dire_wolf',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 70,
    hp: 319,
    level: 13,
    xp: 109,
    color: '#2B2B33',
    width: 80,
    height: 58,
    movementStats: {
      moveSpeed: 94,
      jumpPower: 430,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 340,
      deceleration: 340,
    },
  },
  'owlbear': {
    id: 'owlbear',
    name: 'Owlbear',
    type: 'owlbear',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 524,
    level: 18,
    xp: 149,
    color: '#B5541E',
    width: 58,
    height: 65,
    movementStats: {
      moveSpeed: 115,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 375,
      deceleration: 375,
    },
  },
  'basilisk': {
    id: 'basilisk',
    name: 'Basilisk',
    type: 'basilisk',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 80,
    hp: 620,
    level: 20,
    xp: 165,
    color: '#5C7A4A',
    width: 100,
    height: 44,
    movementStats: {
      moveSpeed: 136,
      jumpPower: 570,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 410,
      deceleration: 410,
    },
  },
  'kobold_worker': {
    id: 'kobold_worker',
    name: 'Kobold Worker',
    type: 'kobold_worker',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 30,
    attackRange: 60,
    hp: 284,
    level: 12,
    xp: 101,
    color: '#A66A3D',
    width: 50,
    height: 52,
    movementStats: {
      moveSpeed: 52,
      jumpPower: 360,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 155,
      deceleration: 155,
    },
  },
  'kobold_tunneler': {
    id: 'kobold_tunneler',
    name: 'Kobold Tunneler',
    type: 'kobold_tunneler',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 30,
    attackRange: 60,
    hp: 319,
    level: 13,
    xp: 109,
    color: '#8B5A2B',
    width: 50,
    height: 52,
    movementStats: {
      moveSpeed: 56,
      jumpPower: 380,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 165,
      deceleration: 165,
    },
  },
  'brigand_thug': {
    id: 'brigand_thug',
    name: 'Brigand Thug',
    type: 'brigand_thug',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 164,
    level: 8,
    xp: 69,
    color: '#6A4F3A',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 60,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 175,
      deceleration: 175,
    },
  },
  'duchess': {
    id: 'duchess',
    name: 'Duchess',
    type: 'duchess',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 300,
    attackRange: 60,
    hp: 852,
    level: 12,
    xp: 505,
    color: '#B65481',
    width: 57,
    height: 65,
    movementStats: {
      moveSpeed: 68,
      jumpPower: 440,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 195,
      deceleration: 195,
    },
  },
  'rimecrest_troll': {
    id: 'rimecrest_troll',
    name: 'Rimecrest Troll',
    type: 'rimecrest_troll',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 15,
    attackRange: 60,
    hp: 571,
    level: 19,
    xp: 157,
    color: '#4F6A80',
    width: 44,
    height: 90,
    movementStats: {
      moveSpeed: 54,
      jumpPower: 370,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 160,
      deceleration: 160,
    },
  },
  'wendigo': {
    id: 'wendigo',
    name: 'Wendigo',
    type: 'wendigo',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 23,
    attackRange: 60,
    hp: 620,
    level: 20,
    xp: 165,
    color: '#B4C9D1',
    width: 58,
    height: 66,
    movementStats: {
      moveSpeed: 60,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 175,
      deceleration: 175,
    },
  },
  'mindless_zombie': {
    id: 'mindless_zombie',
    name: 'Mindless Zombie',
    type: 'mindless_zombie',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 15,
    attackRange: 60,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#78876B',
    width: 52,
    height: 62,
    movementStats: {
      moveSpeed: 52,
      jumpPower: 360,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 155,
      deceleration: 155,
    },
  },
  'ember_vigil_zealot': {
    id: 'ember_vigil_zealot',
    name: 'Ember Vigil Zealot',
    type: 'ember_vigil_zealot',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 524,
    level: 18,
    xp: 149,
    color: '#B8232E',
    width: 56,
    height: 65,
    movementStats: {
      moveSpeed: 66,
      jumpPower: 430,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 190,
      deceleration: 190,
    },
  },
  'sandstinger': {
    id: 'sandstinger',
    name: 'Sandstinger',
    type: 'sandstinger',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 15,
    attackRange: 60,
    hp: 479,
    level: 17,
    xp: 141,
    color: '#C68642',
    width: 60,
    height: 44,
    movementStats: {
      moveSpeed: 76,
      jumpPower: 370,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 310,
      deceleration: 310,
    },
  },
  'voodoo_troll': {
    id: 'voodoo_troll',
    name: 'Rimecrest Hexer',
    type: 'voodoo_troll',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 23,
    attackRange: 60,
    hp: 724,
    level: 22,
    xp: 181,
    color: '#5C4A73',
    width: 50,
    height: 82,
    movementStats: {
      moveSpeed: 60,
      jumpPower: 400,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 175,
      deceleration: 175,
    },
  },
  'brigand_raider': {
    id: 'brigand_raider',
    name: 'Brigand Raider',
    type: 'brigand_raider',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 251,
    level: 11,
    xp: 93,
    color: '#6B4F63',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 74,
      jumpPower: 470,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 210,
      deceleration: 210,
    },
  },
  'scarecrow_golem': {
    id: 'scarecrow_golem',
    name: 'Scarecrow Golem',
    type: 'scarecrow_golem',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 30,
    attackRange: 60,
    hp: 284,
    level: 12,
    xp: 101,
    color: '#B39257',
    width: 56,
    height: 65,
    movementStats: {
      moveSpeed: 78,
      jumpPower: 490,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 220,
      deceleration: 220,
    },
  },
  'stripe_runner': {
    id: 'stripe_runner',
    name: 'Stripe-runner',
    type: 'stripe_runner',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 150,
    respawnTime: 23,
    attackRange: 64,
    hp: 251,
    level: 11,
    xp: 93,
    color: '#D9A441',
    width: 66,
    height: 50,
    movementStats: {
      moveSpeed: 103,
      jumpPower: 460,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 355,
      deceleration: 355,
    },
  },
  'centaur_scout': {
    id: 'centaur_scout',
    name: 'Centaur Scout',
    type: 'centaur_scout',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 78,
    hp: 779,
    level: 23,
    xp: 189,
    color: '#9A8E3C',
    width: 95,
    height: 65,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 225,
      deceleration: 225,
    },
  },
  'thornpaw_pathfinder': {
    id: 'thornpaw_pathfinder',
    name: 'Thornpaw Pathfinder',
    type: 'thornpaw_pathfinder',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 61,
    hp: 479,
    level: 17,
    xp: 141,
    color: '#7A5C3E',
    width: 62,
    height: 65,
    movementStats: {
      moveSpeed: 86,
      jumpPower: 530,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 240,
      deceleration: 240,
    },
  },
  'amberbark_treant': {
    id: 'amberbark_treant',
    name: 'Amberbark Treant',
    type: 'amberbark_treant',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 30,
    attackRange: 60,
    hp: 319,
    level: 13,
    xp: 109,
    color: '#2A3B24',
    width: 60,
    height: 77,
    movementStats: {
      moveSpeed: 90,
      jumpPower: 550,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 250,
      deceleration: 250,
    },
  },
  'naga_siren': {
    id: 'naga_siren',
    name: 'Naga Siren',
    type: 'naga_siren',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 30,
    attackRange: 60,
    hp: 836,
    level: 24,
    xp: 197,
    color: '#6F2FA7',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 225,
      deceleration: 225,
    },
  },
  'brigand_smuggler': {
    id: 'brigand_smuggler',
    name: 'Brigand Smuggler',
    type: 'brigand_smuggler',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 45,
    attackRange: 60,
    hp: 284,
    level: 12,
    xp: 101,
    color: '#58614A',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 225,
      deceleration: 225,
    },
  },
  'brigand_lieutenant': {
    id: 'brigand_lieutenant',
    name: 'Brigand Lieutenant',
    type: 'brigand_lieutenant',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 120,
    respawnTime: 120,
    attackRange: 65,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ffb886' },
    ],
    hp: 638,
    level: 13,
    xp: 273,
    color: '#46525C',
    width: 65,
    height: 85,
    movementStats: {
      moveSpeed: 84,
      jumpPower: 520,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 235,
      deceleration: 235,
    },
  },
  'deepglow_ooze': {
    id: 'deepglow_ooze',
    name: 'Deepglow Ooze',
    type: 'deepglow_ooze',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 100,
    respawnTime: 30,
    attackRange: 60,
    hp: 479,
    level: 17,
    xp: 141,
    color: '#B5A642',
    width: 60,
    height: 52,
    movementStats: {
      moveSpeed: 82,
      jumpPower: 510,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 230,
      deceleration: 230,
    },
  },
  'rabbit': {
    id: 'rabbit',
    name: 'Rabbit',
    type: 'rabbit',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 15,
    attackRange: 60,
    hp: 191,
    level: 9,
    xp: 77,
    color: '#D8C9A3',
    width: 34,
    height: 36,
    movementStats: {
      moveSpeed: 73,
      jumpPower: 360,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 305,
      deceleration: 305,
    },
  },
  'rat': {
    id: 'rat',
    name: 'Rat',
    type: 'rat',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 15,
    attackRange: 60,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#6B5D52',
    width: 48,
    height: 24,
    movementStats: {
      moveSpeed: 73,
      jumpPower: 360,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 305,
      deceleration: 305,
    },
  },
  'deer': {
    id: 'deer',
    name: 'Deer',
    type: 'deer',
    mobType: 'normal',
    aiType: 'passive',
    aggroRange: 0,
    respawnTime: 15,
    attackRange: 60,
    hp: 191,
    level: 9,
    xp: 77,
    color: '#A9714A',
    width: 54,
    height: 48,
    movementStats: {
      moveSpeed: 73,
      jumpPower: 360,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 305,
      deceleration: 305,
    },
  },
  'captain_morrow_vane': {
    id: 'captain_morrow_vane',
    name: 'Captain Morrow Vane',
    type: 'captain_morrow_vane',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 65,
    phases: [
      { hpThreshold: 0.5, name: 'Enraged', damageMultiplier: 1.3, color: '#ffb886' },
      { hpThreshold: 0.2, name: 'Final Stand', damageMultiplier: 1.8, speedMultiplier: 1.5, color: '#ff3300' },
    ],
    hp: 1780,
    level: 14,
    xp: 1170,
    color: '#2E2840',
    width: 65,
    height: 85,
    movementStats: {
      moveSpeed: 92,
      jumpPower: 560,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 255,
      deceleration: 255,
    },
  },
  'gilling_coastrunner': {
    id: 'gilling_coastrunner',
    name: 'Gilling Coastrunner',
    type: 'gilling_coastrunner',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 319,
    level: 13,
    xp: 109,
    color: '#28AFAA',
    width: 50,
    height: 65,
    movementStats: {
      moveSpeed: 62,
      jumpPower: 410,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 180,
      deceleration: 180,
    },
  },
  'scarecrow_warden': {
    id: 'scarecrow_warden',
    name: 'Field Warden',
    type: 'scarecrow_warden',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 319,
    level: 13,
    xp: 109,
    color: '#725027',
    width: 56,
    height: 65,
    movementStats: {
      moveSpeed: 82,
      jumpPower: 510,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 230,
      deceleration: 230,
    },
  },
  'moonhowler_lurker': {
    id: 'moonhowler_lurker',
    name: 'Moonhowler Lurker',
    type: 'moonhowler_lurker',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 395,
    level: 15,
    xp: 125,
    color: '#453A36',
    width: 50,
    height: 69,
    movementStats: {
      moveSpeed: 78,
      jumpPower: 490,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 220,
      deceleration: 220,
    },
  },
  'clodkin_stonecutter': {
    id: 'clodkin_stonecutter',
    name: 'Clodkin Stonecutter',
    type: 'clodkin_stonecutter',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 395,
    level: 15,
    xp: 125,
    color: '#6E6355',
    width: 62,
    height: 53,
    movementStats: {
      moveSpeed: 58,
      jumpPower: 390,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 170,
      deceleration: 170,
    },
  },
  'tuskmire_raider': {
    id: 'tuskmire_raider',
    name: 'Tuskmire Raider',
    type: 'tuskmire_raider',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 68,
    hp: 220,
    level: 10,
    xp: 85,
    color: '#9C6B3E',
    width: 75,
    height: 65,
    movementStats: {
      moveSpeed: 70,
      jumpPower: 450,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'gearwright_logger': {
    id: 'gearwright_logger',
    name: 'Gearwright Logger',
    type: 'gearwright_logger',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 251,
    level: 11,
    xp: 93,
    color: '#6D7632',
    width: 55,
    height: 65,
    movementStats: {
      moveSpeed: 90,
      jumpPower: 550,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 250,
      deceleration: 250,
    },
  },
  'longleg_strider': {
    id: 'longleg_strider',
    name: 'Longleg Strider',
    type: 'longleg_strider',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 59,
    level: 3,
    xp: 29,
    color: '#8C7853',
    width: 50,
    height: 58,
    movementStats: {
      moveSpeed: 79,
      jumpPower: 380,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 315,
      deceleration: 315,
    },
  },
  'canopy_weaver': {
    id: 'canopy_weaver',
    name: 'Canopy Weaver',
    type: 'canopy_weaver',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 60,
    hp: 395,
    level: 15,
    xp: 125,
    color: '#3F6B47',
    width: 60,
    height: 45,
    movementStats: {
      moveSpeed: 79,
      jumpPower: 380,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 315,
      deceleration: 315,
    },
  },
  'murkwater_snapjaw': {
    id: 'murkwater_snapjaw',
    name: 'Murkwater Snapjaw',
    type: 'murkwater_snapjaw',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 90,
    hp: 436,
    level: 16,
    xp: 133,
    color: '#4B481B',
    width: 120,
    height: 40,
    movementStats: {
      moveSpeed: 136,
      jumpPower: 570,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 410,
      deceleration: 410,
    },
  },
  'stormbreak_wyvern': {
    id: 'stormbreak_wyvern',
    name: 'Stormbreak Wyvern',
    type: 'stormbreak_wyvern',
    mobType: 'normal',
    aiType: 'aggressive',
    aggroRange: 108,
    respawnTime: 30,
    attackRange: 80,
    hp: 524,
    level: 18,
    xp: 149,
    color: '#826D68',
    width: 100,
    height: 65,
    movementStats: {
      moveSpeed: 124,
      jumpPower: 530,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 390,
      deceleration: 390,
    },
  },
  'glowspore_keeper': {
    id: 'glowspore_keeper',
    name: 'Rootbound Glowcap Keeper',
    type: 'glowspore_keeper',
    mobType: 'elite',
    aiType: 'juggernaut',
    aggroRange: 180,
    respawnTime: 60,
    abilities: [{ id: 'mushroom_spores', weight: 2 }, { id: 'golem_boulder_toss', weight: 1 }],
    baseDamage: 3,
    attackRange: 90,
    leashRange: 100,
    reactionMs: 650,
    hp: 260,
    level: 5,
    xp: 90,
    color: '#8edec5',
    width: 92,
    height: 84,
    movementStats: {
      moveSpeed: 35,
      jumpPower: 180,
      gravityScale: 1.1,
      terminalVelocity: 1000,
      acceleration: 150,
      deceleration: 600,
    },
  },
  'forest_hollow_crown': {
    id: 'forest_hollow_crown',
    name: 'Hollow Crown',
    type: 'forest_hollow_crown',
    mobType: 'elite',
    aiType: 'juggernaut',
    aggroRange: 220,
    respawnTime: 60,
    abilities: [{ id: 'golem_ground_slam', weight: 1 }],
    baseDamage: 3,
    attackRange: 100,
    leashRange: 220,
    reactionMs: 650,
    attackIntervalMs: 2800,
    socialAggroRange: 0,
    hp: 280,
    level: 8,
    xp: 120,
    color: '#58714b',
    width: 70,
    height: 80,
    movementStats: {
      moveSpeed: 30,
      jumpPower: 180,
      gravityScale: 1.1,
      terminalVelocity: 1000,
      acceleration: 150,
      deceleration: 600,
    },
  },
  'blightroot_alpha': {
    id: 'blightroot_alpha',
    name: 'Blightroot, the Alpha',
    type: 'blightroot_alpha',
    mobType: 'elite',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 90,
    abilities: [{ id: 'blightroot_eruption', weight: 2 }, { id: 'blightroot_spores', weight: 1 }],
    attackRange: 90,
    death: { style: 'shadow', scale: 1.3, durationMs: 1200 },
    hp: 650,
    level: 8,
    xp: 180,
    color: '#2b1a12',
    width: 84,
    height: 96,
    movementStats: {
      moveSpeed: 40,
      jumpPower: 350,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 150,
      deceleration: 150,
    },
  },
  'tuskmire_warchief': {
    id: 'tuskmire_warchief',
    name: 'Warchief Gorrak',
    type: 'tuskmire_warchief',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 80,
    phases: [
      { hpThreshold: 0.5, name: 'Bloodied', damageMultiplier: 1.3, color: '#C8902E' },
      { hpThreshold: 0.2, name: 'Last Charge', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#E04A1A' },
    ],
    hp: 1400,
    level: 11,
    xp: 800,
    color: '#7A4A2A',
    width: 100,
    height: 96,
    movementStats: {
      moveSpeed: 74,
      jumpPower: 460,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 210,
      deceleration: 210,
    },
  },
  'gilling_bogchief': {
    id: 'gilling_bogchief',
    name: 'Bogchief Sloughmaw',
    type: 'gilling_bogchief',
    mobType: 'boss',
    aiType: 'aggressive',
    aggroRange: 180,
    respawnTime: 300,
    attackRange: 80,
    phases: [
      { hpThreshold: 0.5, name: 'Tide Call', damageMultiplier: 1.3, color: '#2FB0A0' },
      { hpThreshold: 0.2, name: 'Last Stand', damageMultiplier: 1.6, speedMultiplier: 1.4, color: '#7FD8C0' },
    ],
    hp: 2395,
    level: 17,
    xp: 1269,
    color: '#3F8F6E',
    width: 80,
    height: 90,
    movementStats: {
      moveSpeed: 66,
      jumpPower: 430,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 190,
      deceleration: 190,
    },
  },
  'tuskmire_grunda': {
    id: 'tuskmire_grunda',
    name: 'Grunda Thornheap',
    type: 'tuskmire_grunda',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 72,
    hp: 660,
    level: 10,
    xp: 425,
    color: '#C49058',
    width: 90,
    height: 78,
    movementStats: {
      moveSpeed: 70,
      jumpPower: 450,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 200,
      deceleration: 200,
    },
  },
  'gilling_glimmergill': {
    id: 'gilling_glimmergill',
    name: 'Glimmergill the Magpie',
    type: 'gilling_glimmergill',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 62,
    hp: 1185,
    level: 15,
    xp: 625,
    color: '#C9B83F',
    width: 60,
    height: 72,
    movementStats: {
      moveSpeed: 62,
      jumpPower: 410,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 180,
      deceleration: 180,
    },
  },
  'sandstinger_sunback': {
    id: 'sandstinger_sunback',
    name: 'Old Sunback',
    type: 'sandstinger_sunback',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 64,
    hp: 1860,
    level: 20,
    xp: 825,
    color: '#F0DDB0',
    width: 75,
    height: 53,
    movementStats: {
      moveSpeed: 76,
      jumpPower: 370,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 310,
      deceleration: 310,
    },
  },
  'stormbreak_galeclaw': {
    id: 'stormbreak_galeclaw',
    name: 'Galeclaw the Stormtorn',
    type: 'stormbreak_galeclaw',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 88,
    hp: 1437,
    level: 17,
    xp: 705,
    color: '#46689E',
    width: 120,
    height: 78,
    movementStats: {
      moveSpeed: 124,
      jumpPower: 530,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 390,
      deceleration: 390,
    },
  },
  'rimecrest_brakka': {
    id: 'rimecrest_brakka',
    name: 'Brakka Icetusk',
    type: 'rimecrest_brakka',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 62,
    hp: 2337,
    level: 23,
    xp: 945,
    color: '#4A548C',
    width: 52,
    height: 108,
    movementStats: {
      moveSpeed: 54,
      jumpPower: 370,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 160,
      deceleration: 160,
    },
  },
  'moonhowler_tobin': {
    id: 'moonhowler_tobin',
    name: 'Tobin Ashfur',
    type: 'moonhowler_tobin',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 62,
    hp: 1308,
    level: 16,
    xp: 665,
    color: '#B0B2BC',
    width: 60,
    height: 83,
    movementStats: {
      moveSpeed: 78,
      jumpPower: 490,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 220,
      deceleration: 220,
    },
  },
  'ember_vigil_aldous': {
    id: 'ember_vigil_aldous',
    name: 'Censor Aldous',
    type: 'ember_vigil_aldous',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 64,
    hp: 2013,
    level: 21,
    xp: 865,
    color: '#D0602C',
    width: 67,
    height: 78,
    movementStats: {
      moveSpeed: 66,
      jumpPower: 430,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 190,
      deceleration: 190,
    },
  },
  'thornpaw_mossclaw': {
    id: 'thornpaw_mossclaw',
    name: 'Grandmother Mossclaw',
    type: 'thornpaw_mossclaw',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 66,
    hp: 1713,
    level: 19,
    xp: 785,
    color: '#4F7A5A',
    width: 74,
    height: 78,
    movementStats: {
      moveSpeed: 86,
      jumpPower: 530,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 240,
      deceleration: 240,
    },
  },
  'clodkin_chertjaw': {
    id: 'clodkin_chertjaw',
    name: 'Chertjaw the Seam-Eater',
    type: 'clodkin_chertjaw',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 64,
    hp: 1437,
    level: 17,
    xp: 705,
    color: '#786A8E',
    width: 75,
    height: 64,
    movementStats: {
      moveSpeed: 58,
      jumpPower: 390,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 170,
      deceleration: 170,
    },
  },
  'fire_drake_ashkettle': {
    id: 'fire_drake_ashkettle',
    name: 'Ashkettle the Hissing',
    type: 'fire_drake_ashkettle',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 72,
    hp: 1713,
    level: 19,
    xp: 785,
    color: '#9E4A32',
    width: 84,
    height: 62,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 750,
      gravityScale: 0.5,
      terminalVelocity: 600,
      acceleration: 225,
      deceleration: 225,
    },
  },
  'centaur_sorrel': {
    id: 'centaur_sorrel',
    name: 'Sorrel Dunehoof',
    type: 'centaur_sorrel',
    mobType: 'rare',
    aiType: 'aggressive',
    aggroRange: 140,
    respawnTime: 300,
    attackRange: 86,
    hp: 2868,
    level: 26,
    xp: 1065,
    color: '#B8703E',
    width: 114,
    height: 78,
    movementStats: {
      moveSpeed: 80,
      jumpPower: 500,
      gravityScale: 1,
      terminalVelocity: 1000,
      acceleration: 225,
      deceleration: 225,
    },
  },
};

export const ENTITY_CONFIG = {
  NPC: NPC_CONFIG,
  MOB: MOB_CONFIG,
};
